/**
 * Visualizations Module
 * Handles D3.js visualizations including Sankey diagrams and analytics charts
 * Based on working implementation from backup
 */

import * as d3 from 'd3';
import * as d3Sankey from 'd3-sankey';
import { getCurrentBudgetData, getCurrentBudgetId, captureBudgetData, getCurrentMonthRecurringExpenses, generateAllRecurringExpenses, convertToMonthlyAmount, getTotalIncome } from './budget.js';
import { getCurrentUser } from './auth.js';
import { getLinkingState } from './linkSharing.js';
import { incomeManager } from './incomeManagement.js';
import { db } from './firebase.js';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, deleteDoc, limit, addDoc } from 'firebase/firestore';
import { formatCurrency, formatCurrencyClean, parseCurrencyInput, setupCurrencyInput } from './utils/currency.js';

/**
 * Get the user-scoped expense collection reference
 * Uses new user-scoped structure: users/{userId}/expenses
 */
function getUserExpenseCollection() {
  const user = getCurrentUser();
  if (!user) {
    throw new Error('No user authenticated');
  }
  
  // Check if user is linked to another account
  const linkState = getLinkingState();
  const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
  
  const expenseCollection = collection(db, `users/${targetUserId}/expenses`);
  
  // Test simple query performance
  window.testExpenseQuery = async function() {
    console.log("🧪 [Test] Starting simple expense query test...");
    const testStart = performance.now();
    const testQuery = query(expenseCollection, limit(1));
    const testSnapshot = await getDocs(testQuery);
    const testEnd = performance.now();
    console.log(`🧪 [Test] Simple query (limit 1) took ${(testEnd - testStart).toFixed(2)}ms`);
    console.log(`🧪 [Test] Query path: users/${targetUserId}/expenses`);
    return { timeMs: testEnd - testStart, docs: testSnapshot.size };
  };
  
  return expenseCollection;
}

/**
 * Migrate localStorage starting balance to Firebase (one-time migration)
 */
async function migrateLocalStorageBalance() {
  try {
    const user = getCurrentUser();
    if (!user) return false;
    
    // Check if user is linked to another account
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
    
    const storedBalance = localStorage.getItem(`startingBalance_${targetUserId}`);
    const storedDate = localStorage.getItem(`startingBalanceDate_${targetUserId}`);
    
    if (storedBalance !== null) {
      const amount = parseFloat(storedBalance) || 0;
      const date = storedDate ? new Date(storedDate) : new Date();
      
      console.log(`Migrating localStorage balance: $${amount} from ${date.toLocaleDateString()} for user ${targetUserId}`);
      
      await saveStartingBalanceUpdate(amount, date, targetUserId);
      
      // Clean up localStorage after successful migration
      localStorage.removeItem(`startingBalance_${targetUserId}`);
      localStorage.removeItem(`startingBalanceDate_${targetUserId}`);
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error migrating localStorage balance:', error);
    return false;
  }
}

/**
 * Save starting balance update to Firebase incomeUpdate subcollection
 * @param {number} amount - The account balance amount
 * @param {Date} date - The date of this balance update
 * @param {string} targetUserId - Optional target user ID (for linked accounts)
 */
async function saveStartingBalanceUpdate(amount, date = new Date(), targetUserId = null) {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('No user authenticated');
    }
    
    // Use provided targetUserId or determine from linking state
    const userId = targetUserId || (() => {
      const linkState = getLinkingState();
      return linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
    })();
    
    const incomeUpdateCollection = collection(db, `users/${userId}/incomeUpdate`);
    
    const balanceUpdate = {
      amount: amount,
      date: date.toISOString().split('T')[0], // Store as YYYY-MM-DD string
      timestamp: new Date(), // Full timestamp for ordering
      updatedBy: user.uid // Track who made the update (useful for linked accounts)
    };
    
    await addDoc(incomeUpdateCollection, balanceUpdate);
    console.log(`Starting balance updated: $${amount} on ${date.toLocaleDateString()} for user ${userId}`);
    
    return true;
  } catch (error) {
    console.error('Error saving starting balance update:', error);
    throw error;
  }
}

/**
 * Visualization State
 */
let visualizationState = {
  currentChart: null,
  chartContainer: null,
  chartData: null,
  isMobile: false,
  isRendering: false,
  expandedCategory: null,
  barChartMode: 'category' // Track bar chart mode: 'category' or 'expense-item'
};

/**
 * Get number of divisions for frequency markers
 */
function getFrequencyDivisions(frequency) {
  const frequencyDivisions = {
    'weekly': 4.33, // ~4 weeks per month
    'bi-weekly': 2.17, // ~2 bi-weekly periods per month  
    'monthly': 1,
    'quarterly': 0.33, // Less than monthly, no markers needed
    'annual': 0.083, // Less than monthly, no markers needed
    'one-time': 1, // No markers for one-time
    'plan-it': 1 // No markers for plan-it
  };
  
  const divisions = frequencyDivisions[frequency] || 1;
  
  // Only show markers for frequencies more frequent than monthly
  if (divisions > 1) {
    return Math.round(divisions);
  }
  
  return 1; // No markers
}

/**
 * Get monthly amount for a specific income item by finding it in the DOM
 * and applying frequency/tax adjustments
 */
function getIncomeItemMonthlyAmount(incomeLabel) {
  const incomeItems = document.querySelectorAll('#income-items .input-group');
  
  for (const item of incomeItems) {
    const label = item.querySelector('label')?.textContent?.trim();
    if (label === incomeLabel) {
      const input = item.querySelector('.income-input');
      const frequencySelect = item.querySelector('.frequency-select');
      
      if (!input) continue;
      
      const amount = parseCurrencyInput(input.value);
      const frequency = frequencySelect ? frequencySelect.value : 'monthly';
      
      // Convert to monthly amount using the same logic as getTotalIncome
      let monthlyAmount = convertToMonthlyAmount(amount, frequency, item);
      
      // Apply tax calculations if this is pre-tax income
      const taxType = item.getAttribute('data-tax-type') || 'post-tax';
      if (taxType === 'pre-tax') {
        const federalRate = parseFloat(item.getAttribute('data-federal-tax')) || 22;
        const stateRate = parseFloat(item.getAttribute('data-state-tax')) || 5;
        const ficaRate = 7.65; // Fixed FICA rate
        const totalTaxRate = federalRate + stateRate + ficaRate;
        
        // Calculate net amount (after taxes)
        monthlyAmount = monthlyAmount * (1 - totalTaxRate / 100);
      }
      
      return monthlyAmount;
    }
  }
  
  // Fallback to raw amount if not found in DOM
  return 0;
}

/**
 * Initialize visualization module
 */
export async function initializeVisualizations() {
  console.log('Initializing visualizations module...');
  
  try {
    // Set up chart container
    setupChartContainer();
    
    // Set up mobile detection
    visualizationState.isMobile = window.innerWidth <= 768;
    
    // Set up resize listener
    setupResizeListener();
    
    // Set up keyboard listeners
    setupKeyboardListeners();
    
    console.log('Visualizations module initialized successfully');
  } catch (error) {
    console.error('Failed to initialize visualizations:', error);
  }
}

/**
 * Setup chart container
 */
function setupChartContainer() {
  const container = document.getElementById('sankey-container');
  if (!container) {
    console.warn('Sankey container not found');
    return;
  }
  
  visualizationState.chartContainer = container;
}

/**
 * Setup resize listener for responsive charts
 */
function setupResizeListener() {
  let resizeTimeout;
  window.addEventListener('resize', function() {
    visualizationState.isMobile = window.innerWidth <= 768;
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      console.log('Window resized, re-rendering Sankey chart');
      renderSankeyDiagram();
    }, 300);
  });
}

/**
 * Setup keyboard listeners for visualization controls
 */
function setupKeyboardListeners() {
  document.addEventListener('keydown', function(event) {
    // Close expanded view on Escape key
    if (event.key === 'Escape' && visualizationState.expandedCategory) {
      console.log('Escape pressed, closing expanded view');
      visualizationState.expandedCategory = null;
      renderSankeyDiagram();
      event.preventDefault();
    }
  });
}

/**
 * Convert budget data to Sankey format
 * Based on working implementation from backup
 */
function convertToSankeyData(budgetData) {
  const nodes = [];
  const links = [];
  
  // Filter out zero values first
  const validIncome = budgetData.income.filter(income => (income.amount || income.value || 0) > 0);
  const validCategories = budgetData.categories.filter(category => 
    category.items.some(item => (item.amount || item.value || 0) > 0)
  );
  
  if (validIncome.length === 0 || validCategories.length === 0) {
    return { nodes: [], links: [] };
  }
  
  // Calculate total income using frequency adjustments from DOM
  const totalIncome = getTotalIncome();
  let totalExpenses = 0;
  
  // Build nodes array in the exact order d3-sankey expects
  let nodeIndex = 0;
  const nodeMap = new Map();
  
  // 1. Add all income source nodes first (with frequency adjustments)
  validIncome.forEach(income => {
    // Get the monthly-adjusted amount for this income item from DOM
    const monthlyValue = getIncomeItemMonthlyAmount(income.label);
    const node = {
      id: nodeIndex,
      name: income.label,
      type: 'income-source',
      value: monthlyValue,
      originalValue: monthlyValue
    };
    nodes.push(node);
    nodeMap.set(`income_${income.label}`, nodeIndex);
    nodeIndex++;
  });
  
  // 2. Add intermediary "Total Income" node
  const totalIncomeNode = {
    id: nodeIndex,
    name: 'Total Income',
    type: 'total-income',
    value: totalIncome,
    originalValue: totalIncome
  };
  nodes.push(totalIncomeNode);
  nodeMap.set('total_income', nodeIndex);
  nodeIndex++;
  
  // 3. Collect all expense items with their monthly values
  const allItems = [];
  validCategories.forEach(category => {
    const cleanName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
    const validItems = category.items.filter(item => (item.amount || item.value || 0) > 0);
    
    validItems.forEach(item => {
      const rawAmount = parseFloat(item.amount || item.value) || 0;
      const frequency = item.frequency || 'monthly';
      const monthlyItemValue = convertToMonthlyAmount(rawAmount, frequency);
      
      allItems.push({
        categoryName: cleanName,
        categoryOriginalName: category.name,
        itemName: item.label,
        monthlyValue: monthlyItemValue,
        originalValue: rawAmount,
        frequency: frequency,
        item: item
      });
    });
  });

  // Sort all items by monthly value (descending) and take top items
  allItems.sort((a, b) => b.monthlyValue - a.monthlyValue);
  const TOP_ITEMS_LIMIT = 15; // Easy to adjust
  const topItems = allItems.slice(0, TOP_ITEMS_LIMIT);
  const topItemKeys = new Set(topItems.map(item => `${item.categoryOriginalName}_${item.itemName}`));

  // Group items by category for building nodes
  const categoryData = new Map();
  validCategories.forEach(category => {
    const cleanName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
    const validItems = category.items.filter(item => (item.amount || item.value || 0) > 0);
    
    let categoryTotal = 0;
    const topItemsInCategory = [];
    let otherItemsTotal = 0;
    
    const otherItemsInCategory = [];
    
    validItems.forEach(item => {
      const rawAmount = parseFloat(item.amount || item.value) || 0;
      const frequency = item.frequency || 'monthly';
      const monthlyItemValue = convertToMonthlyAmount(rawAmount, frequency);
      categoryTotal += monthlyItemValue;
      
      const itemKey = `${category.name}_${item.label}`;
      if (topItemKeys.has(itemKey)) {
        topItemsInCategory.push({
          name: item.label,
          monthlyValue: monthlyItemValue,
          originalValue: rawAmount,
          frequency: frequency
        });
      } else {
        otherItemsTotal += monthlyItemValue;
        otherItemsInCategory.push({
          name: item.label,
          monthlyValue: monthlyItemValue,
          originalValue: rawAmount,
          frequency: frequency
        });
      }
    });
    
    if (categoryTotal > 0) {
      categoryData.set(cleanName, {
        categoryOriginalName: category.name,
        total: categoryTotal,
        topItems: topItemsInCategory,
        otherTotal: otherItemsTotal,
        otherItems: otherItemsInCategory
      });
      totalExpenses += categoryTotal;
    }
  });

  // 4. Collect category data first, then add nodes in specific order
  const regularCategories = [];
  let unallocatedData = null;
  
  // Calculate surplus first
  const surplus = totalIncome - totalExpenses;
  if (surplus > 0) {
    unallocatedData = {
      name: 'Unallocated',
      type: 'category',
      value: surplus,
      originalValue: surplus,
      isUnallocated: true
    };
    totalExpenses += surplus; // Update total to match income
  }

  // 4. Add all category nodes first (layer 3)
  const categoryEntries = Array.from(categoryData.entries());
  categoryEntries.forEach(([cleanName, catData]) => {
    const categoryNode = {
      id: nodeIndex,
      name: cleanName,
      type: 'category',
      value: catData.total,
      originalValue: catData.total
    };
    nodes.push(categoryNode);
    nodeMap.set(`category_${catData.categoryOriginalName}`, nodeIndex);
    nodeIndex++;
  });

  // 5. Add "Unallocated" category (at bottom of layer 3)
  if (unallocatedData) {
    const unallocatedNode = {
      id: nodeIndex,
      ...unallocatedData
    };
    nodes.push(unallocatedNode);
    nodeMap.set('category_Unallocated', nodeIndex);
    nodeIndex++;
  }

  // 6. Add all items and "Other" nodes grouped by category (layer 4)
  categoryEntries.forEach(([cleanName, catData]) => {
    // Add individual top items for this category
    catData.topItems.forEach(item => {
      const itemNode = {
        id: nodeIndex,
        name: item.name,
        type: 'item',
        value: item.monthlyValue,
        originalValue: item.originalValue,
        frequency: item.frequency,
        categoryName: cleanName
      };
      nodes.push(itemNode);
      nodeMap.set(`item_${catData.categoryOriginalName}_${item.name}`, nodeIndex);
      nodeIndex++;
    });

    // Add "Other" node if there are remaining items
    if (catData.otherTotal > 0) {
      const otherNode = {
        id: nodeIndex,
        name: `${cleanName} - Other`,
        type: 'other',
        value: catData.otherTotal,
        originalValue: catData.otherTotal,
        categoryName: cleanName,
        isOtherGroup: true,
        otherItems: catData.otherItems || []
      };
      nodes.push(otherNode);
      nodeMap.set(`other_${catData.categoryOriginalName}`, nodeIndex);
      nodeIndex++;
    }
  });

  // 7. Add "Unallocated" item to match the category (layer 4)
  if (unallocatedData) {
    const unallocatedItemNode = {
      id: nodeIndex,
      name: 'Surplus',
      type: 'item',
      value: unallocatedData.value,
      originalValue: unallocatedData.originalValue,
      categoryName: 'Unallocated',
      isUnallocatedItem: true
    };
    nodes.push(unallocatedItemNode);
    nodeMap.set('item_Unallocated_Surplus', nodeIndex);
    nodeIndex++;
  }
  
  // Build links with integer node IDs
  if (totalIncome > 0 && totalExpenses > 0) {
    // 1. Income sources to Total Income (using frequency-adjusted amounts)
    validIncome.forEach(income => {
      const incomeValue = getIncomeItemMonthlyAmount(income.label);
      if (incomeValue > 0) {
        links.push({
          source: nodeMap.get(`income_${income.label}`),
          target: nodeMap.get('total_income'),
          value: incomeValue
        });
      }
    });
    
    // 2. Total Income to categories
    categoryData.forEach((catData, cleanName) => {
      // Link Total Income to category
      links.push({
        source: nodeMap.get('total_income'),
        target: nodeMap.get(`category_${catData.categoryOriginalName}`),
        value: catData.total
      });
      
      // Link category to individual top items
      catData.topItems.forEach(item => {
        links.push({
          source: nodeMap.get(`category_${catData.categoryOriginalName}`),
          target: nodeMap.get(`item_${catData.categoryOriginalName}_${item.name}`),
          value: item.monthlyValue
        });
      });
      
      // Link category to "Other" group if it exists
      if (catData.otherTotal > 0) {
        links.push({
          source: nodeMap.get(`category_${catData.categoryOriginalName}`),
          target: nodeMap.get(`other_${catData.categoryOriginalName}`),
          value: catData.otherTotal
        });
      }
    });
    
    // 3. Total Income to Unallocated (if surplus exists)
    if (surplus > 0) {
      links.push({
        source: nodeMap.get('total_income'),
        target: nodeMap.get('category_Unallocated'),
        value: surplus
      });
      
      // Link Unallocated category to its Surplus item
      links.push({
        source: nodeMap.get('category_Unallocated'),
        target: nodeMap.get('item_Unallocated_Surplus'),
        value: surplus
      });
    }
  }
  
  // Validate data structure
  const isValid = nodes.every(node => 
    typeof node.id === 'number' && 
    typeof node.name === 'string' && 
    node.name.length > 0
  ) && links.every(link => 
    typeof link.source === 'number' && 
    typeof link.target === 'number' && 
    typeof link.value === 'number' && 
    link.value > 0 &&
    link.source < nodes.length &&
    link.target < nodes.length
  );
  
  console.log('Sankey data validation (4-column layout with top 25 items + Other):', {
    isValid,
    nodeCount: nodes.length,
    linkCount: links.length,
    incomeSourceNodes: nodes.filter(n => n.type === 'income-source').length,
    totalIncomeNodes: nodes.filter(n => n.type === 'total-income').length,
    categoryNodes: nodes.filter(n => n.type === 'category').length,
    itemNodes: nodes.filter(n => n.type === 'item').length,
    otherNodes: nodes.filter(n => n.type === 'other').length,
    top25Items: nodes.filter(n => n.type === 'item').map(n => n.name),
    sampleNode: nodes[0],
    sampleLink: links[0]
  });
  
  return { nodes, links };
}

/**
 * Render Sankey diagram
 * Main function that orchestrates the visualization rendering
 */
export async function renderSankeyDiagram() {
  console.log('=== SANKEY CHART RENDER START (Mobile Enhanced) ===');
  
  // Prevent duplicate rendering
  if (visualizationState.isRendering) {
    console.log('Already rendering, skipping...');
    return;
  }
  visualizationState.isRendering = true;
  
  // Ensure mobile state is set
  if (visualizationState.isMobile === undefined) {
    visualizationState.isMobile = window.innerWidth <= 768;
    console.log('Mobile state initialized:', visualizationState.isMobile);
  }
  
  // Check for mobile portrait mode and setup tabs
  const isPortrait = window.innerWidth < window.innerHeight;
  const isMobilePortrait = visualizationState.isMobile && isPortrait;
  
  setupMobileTabs(isMobilePortrait);
  
  if (typeof captureBudgetData !== 'function') {
    console.error('captureBudgetData function not found!');
    visualizationState.isRendering = false;
    return;
  }
  
  const budgetData = captureBudgetData();
  console.log('Budget data captured:', budgetData);
  
  const sankeyData = convertToSankeyData(budgetData);
  console.log('Sankey data converted:', sankeyData);
  
  // Filter data for mobile tabs if needed
  const filteredSankeyData = filterDataForMobileTab(sankeyData);
  console.log('Filtered sankey data for mobile tab:', currentMobileTab, filteredSankeyData);
  
  // Update flow summary cards
  updateFlowSummaryCards(budgetData);
  
  // Render projected balance chart
  await renderProjectedBalance();
  
  // Clear previous chart
  d3.select("#sankey-container").selectAll("*").remove();
  
  // Check if we have data to display
  const totalFlow = budgetData.income.reduce((sum, income) => sum + (income.amount || income.value || 0), 0);
  
  console.log('Data detection check:', {
    budgetData,
    sankeyDataNodes: sankeyData.nodes.length,
    sankeyDataLinks: sankeyData.links.length,
    filteredNodes: filteredSankeyData.nodes.length,
    filteredLinks: filteredSankeyData.links.length,
    totalFlow,
    incomeItems: budgetData.income.length,
    categoryItems: budgetData.categories.length
  });
  
  if (filteredSankeyData.nodes.length === 0 || filteredSankeyData.links.length === 0 || totalFlow === 0) {
    console.log('No data detected, showing empty state');
    showEmptyState();
    visualizationState.isRendering = false;
    return;
  }
  
  // Mobile-aware container setup
  const container = document.getElementById('sankey-container');
  if (!container) {
    console.error('Sankey container not found');
    visualizationState.isRendering = false;
    return;
  }
  
  // Wait for proper container dimensions
  setTimeout(() => {
    const isMobile = visualizationState.isMobile;
    const containerRect = container.getBoundingClientRect();
    
    // Mobile-optimized dimensions
    const margin = isMobile 
      ? { top: 15, right: 15, bottom: 15, left: 15 }
      : { top: 30, right: 50, bottom: 30, left: 50 };
    
    // Ensure minimum viable dimensions
    const minWidth = isMobile ? 280 : 400;
    const minHeight = isMobile ? 350 : 400;
    
    const containerWidth = Math.max(containerRect.width || container.offsetWidth, minWidth);
    const containerHeight = Math.max(containerRect.height || container.offsetHeight, minHeight);
    
    console.log('Mobile-aware chart dimensions:', { 
      containerWidth, 
      containerHeight, 
      isMobile,
      originalRect: containerRect
    });
    
    if (containerWidth <= 50 || containerHeight <= 50) {
      console.warn('Container too small, forcing minimum dimensions');
      const fallbackWidth = isMobile ? 280 : 500;
      const fallbackHeight = isMobile ? 350 : 400;
      createSankeyWithDimensions(fallbackWidth, fallbackHeight, margin, isMobile, filteredSankeyData);
      visualizationState.isRendering = false;
      return;
    }
    
    createSankeyWithDimensions(containerWidth, containerHeight, margin, isMobile, filteredSankeyData);
    
    // Render bar chart underneath Sankey
    // Initialize with category mode if not set
    if (!visualizationState.barChartMode) {
      visualizationState.barChartMode = 'category';
    }
    
    if (visualizationState.barChartMode === 'category') {
      renderCategoryBarChart(budgetData);
    } else {
      renderExpenseItemBarChart(budgetData);
    }
    
    visualizationState.isRendering = false;
    
  }, visualizationState.isMobile ? 100 : 50);
}

/**
 * Update Sankey statistics display
 */

/**
 * Show empty state when no data is available
 */
function showEmptyState() {
  d3.select("#sankey-container")
    .append("div")
    .attr("class", "sankey-empty-state")
    .html(`
      <div class="icon">📊</div>
      <div class="message">No budget data to visualize</div>
      <div class="submessage">Enter your income and expenses in the Budget view to see the money flow</div>
    `);
}

/**
 * Create manual layout when Sankey algorithm fails
 */
function createManualLayout(graph, width, height) {
  console.log('Creating manual layout...');
  
  // Group nodes by type
  const incomeNodes = graph.nodes.filter(n => n.type === 'income');
  const categoryNodes = graph.nodes.filter(n => n.type === 'category');
  const itemNodes = graph.nodes.filter(n => n.type === 'item');
  
  const nodeWidth = 25;
  const columnSpacing = width / 3;
  
  // Calculate total values for proportional sizing
  const totalIncomeValue = incomeNodes.reduce((sum, n) => sum + n.value, 0);
  const totalCategoryValue = categoryNodes.reduce((sum, n) => sum + n.value, 0);
  const totalItemValue = itemNodes.reduce((sum, n) => sum + n.value, 0);
  
  // Position income nodes (left column)
  let currentY = 20;
  incomeNodes.forEach(node => {
    const nodeHeight = Math.max(10, (node.value / totalIncomeValue) * (height - 100));
    node.x0 = 0;
    node.x1 = nodeWidth;
    node.y0 = currentY;
    node.y1 = currentY + nodeHeight;
    currentY += nodeHeight + 10;
  });
  
  // Position category nodes (middle column)
  currentY = 20;
  categoryNodes.forEach(node => {
    const nodeHeight = Math.max(10, (node.value / totalCategoryValue) * (height - 100));
    node.x0 = columnSpacing;
    node.x1 = columnSpacing + nodeWidth;
    node.y0 = currentY;
    node.y1 = currentY + nodeHeight;
    currentY += nodeHeight + 10;
  });
  
  // Position item nodes (right column)
  currentY = 20;
  itemNodes.forEach(node => {
    const nodeHeight = Math.max(10, (node.value / totalItemValue) * (height - 100));
    node.x0 = columnSpacing * 2;
    node.x1 = columnSpacing * 2 + nodeWidth;
    node.y0 = currentY;
    node.y1 = currentY + nodeHeight;
    currentY += nodeHeight + 10;
  });
  
  console.log('Manual layout complete');
}


/**
 * Helper function to create Sankey with specific dimensions
 */
function createSankeyWithDimensions(containerWidth, containerHeight, margin, isMobile, filteredData = null) {
  const budgetData = captureBudgetData();
  const sankeyData = filteredData || convertToSankeyData(budgetData);
  
  // Calculate dynamic height based on number of expense items for better spacing
  const itemAndOtherNodes = sankeyData.nodes.filter(n => n.type === 'item' || n.type === 'other').length;
  const minHeight = 600;
  const dynamicHeight = Math.max(minHeight, itemAndOtherNodes * 30 + 300); // 30px per item/other node + 300px buffer
  
  // Use dynamic height instead of container height
  const actualHeight = Math.max(containerHeight, dynamicHeight);
  const width = containerWidth - margin.left - margin.right;
  const height = actualHeight - margin.top - margin.bottom;
  
  console.log('Dynamic height calculation:', {
    itemAndOtherNodes,
    minHeight,
    dynamicHeight,
    containerHeight,
    actualHeight,
    finalHeight: height
  });
  const totalFlow = budgetData.income.reduce((sum, income) => sum + (income.amount || income.value || 0), 0);
  
  try {
    console.log('Creating Sankey chart with dimensions:', {width, height, containerWidth, containerHeight});
    console.log('D3 Sankey available:', d3Sankey);
    
    // Create responsive SVG with dynamic height
    const svg = d3.select("#sankey-container")
      .append("svg")
      .attr("width", containerWidth)
      .attr("height", actualHeight)
      .attr("viewBox", `0 0 ${containerWidth} ${actualHeight}`)
      .style("max-width", "100%")
      .style("height", isMobile ? "auto" : actualHeight + "px")
      .on("click", function(event) {
        // Close expanded view when clicking on empty space
        if (event.target === this && visualizationState.expandedCategory) {
          console.log('Clicked on empty space, closing expanded view');
          visualizationState.expandedCategory = null;
          renderSankeyDiagram();
        }
      });
    
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // Pure D3 Sankey - let it handle everything naturally
    console.log('Creating pure D3 Sankey generator...');
    const sankey = d3Sankey.sankey()
      .nodeId(d => d.id)
      .nodeAlign(d3Sankey.sankeyJustify)
      .nodeWidth(25)
      .nodePadding(20)
      .nodeSort(null)
      .extent([[0, 0], [width, height]]);
    
    const graph = {
      nodes: sankeyData.nodes.map(d => ({ ...d })),
      links: sankeyData.links.map(d => ({ ...d }))
    };
    
    console.log('Graph before sankey processing:', graph);
    console.log('Sankey dimensions:', { width, height, containerWidth, containerHeight });
    console.log('Total nodes:', graph.nodes.length, 'Total links:', graph.links.length);
    
    // Ensure nodes have proper values for the sankey algorithm
    graph.nodes.forEach(node => {
      if (!node.value && node.originalValue) {
        node.value = node.originalValue;
      }
    });

    
    console.log('Running Sankey algorithm...');
    console.log('Graph before sankey:', {
      nodes: graph.nodes.length,
      links: graph.links.length,
      firstNode: graph.nodes[0],
      firstLink: graph.links[0]
    });
    
    sankey(graph);
    
    console.log('Graph after sankey processing:', graph);
    console.log('Node positions check:', {
      firstNodePosition: {
        name: graph.nodes[0]?.name,
        x0: graph.nodes[0]?.x0,
        x1: graph.nodes[0]?.x1,
        y0: graph.nodes[0]?.y0,
        y1: graph.nodes[0]?.y1,
        value: graph.nodes[0]?.value
      },
      allNodesHavePositions: graph.nodes.every(n => n.x0 !== undefined && n.y0 !== undefined),
      sankeyWorked: graph.nodes.some(n => (n.y1 - n.y0) > 0)
    });
    
    // Debug the node positions after processing
    graph.nodes.forEach(node => {
      console.log(`Final node ${node.name}: x0=${node.x0}, x1=${node.x1}, y0=${node.y0}, y1=${node.y1}, value=${node.value}`);
    });
    
    // Color scale for 4-column layout with top 25 items + Other groups
    // Create category-based color mapping
    const categoryColors = [
      '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
      '#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#16a085',
      '#27ae60', '#2980b9', '#8e44ad', '#f1c40f', '#c0392b'
    ];
    
    // Get unique category names from nodes
    const categories = Array.from(new Set(
      graph.nodes
        .filter(d => d.type === 'category' && !d.isUnallocated)
        .map(d => d.name)
    ));
    
    // Create color mapping for categories
    const categoryColorMap = new Map();
    categories.forEach((cat, index) => {
      categoryColorMap.set(cat, categoryColors[index % categoryColors.length]);
    });
    
    // Function to get node color based on category
    function getNodeColor(node) {
      if (node.type === 'income-source') return '#2ecc71';
      if (node.type === 'total-income') return '#27ae60';
      if (node.isUnallocated) return '#95a5a6';
      if (node.type === 'category') {
        return categoryColorMap.get(node.name) || '#3498db';
      }
      if (node.type === 'item' || node.type === 'other') {
        return categoryColorMap.get(node.categoryName) || '#3498db';
      }
      return '#3498db';
    }
    
    // Create gradients
    const defs = svg.append("defs");
    graph.links.forEach((link, i) => {
      const gradient = defs.append("linearGradient")
        .attr("id", `gradient-${i}`)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", link.source.x1)
        .attr("x2", link.target.x0);
      
      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", getNodeColor(link.source))
        .attr("stop-opacity", 0.6);
      
      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", getNodeColor(link.target))
        .attr("stop-opacity", 0.6);
    });
    
    // Draw links with mobile-friendly width
    g.append("g")
      .selectAll("path")
      .data(graph.links)
      .join("path")
      .attr("d", d3Sankey.sankeyLinkHorizontal())
      .attr("stroke", (d, i) => `url(#gradient-${i})`)
      .attr("stroke-width", d => {
        console.log(`Link ${d.source.name} → ${d.target.name}: value=${d.value}, d.width=${d.width}`);
        return Math.max(0.5, d.width); // Use D3's calculated width
      })
      .attr("fill", "none")
      .attr("opacity", 0.7)
      .style("cursor", "pointer")
      .on("mouseover touchstart", function(event, d) {
        d3.select(this).attr("opacity", 0.9);
        const percentage = ((d.value / totalFlow) * 100).toFixed(1);
        showSankeyTooltip(event, `
          <div style="font-weight: 600; margin-bottom: 4px;">
            ${d.source.name} → ${d.target.name}
          </div>
          <div style="color: #27ae60; font-weight: 700; font-size: 1.1em;">
            ${formatCurrencyClean(d.value)}
          </div>
          <div style="color: #6c757d; font-size: 0.9em;">
            ${percentage}% of total flow
          </div>
        `);
      })
      .on("mouseout touchend", function() {
        d3.select(this).attr("opacity", 0.7);
        hideSankeyTooltip();
      });
    
    // Draw nodes - pure D3 Sankey positioning
    console.log('Drawing nodes with pure D3 positioning:', graph.nodes.length);
    g.append("g")
      .selectAll("rect")
      .data(graph.nodes)
      .join("rect")
      .attr("x", d => d.x0)
      .attr("y", d => d.y0)
      .attr("height", d => d.y1 - d.y0)
      .attr("width", d => d.x1 - d.x0)
      .attr("fill", d => getNodeColor(d))
      .attr("stroke", "#fff")
      .attr("stroke-width", 1)
      .attr("rx", 2)
      .attr("ry", 2)
      .style("cursor", "default")
      .on("mouseover touchstart", function(event, d) {
        d3.select(this).attr("fill", d3.color(getNodeColor(d)).darker(0.3));
        showNodeTooltip(event, d, budgetData, totalFlow, getNodeColor);
      })
      .on("mouseout touchend", function(event, d) {
        d3.select(this).attr("fill", getNodeColor(d));
        hideSankeyTooltip();
      })
      .on("click", function(event, d) {
        handleNodeClick(d);
      });
    
    // Add node labels - pure D3 positioning
    g.append("g")
      .selectAll("text")
      .data(graph.nodes)
      .join("text")
      .attr("x", d => d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6)
      .attr("y", d => (d.y0 + d.y1) / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", d => d.x0 < width / 2 ? "start" : "end")
      .text(d => d.name)
      .style("font-size", "11px")
      .style("font-weight", "600")
      .style("fill", "#2c3e50")
      .style("cursor", "default")
      .on("mouseover touchstart", function(event, d) {
        showNodeTooltip(event, d, budgetData, totalFlow, getNodeColor);
      })
      .on("mouseout touchend", function() {
        hideSankeyTooltip();
      })
      .on("click", function(event, d) {
        handleNodeClick(d);
      });
    
    console.log('=== MOBILE-ENHANCED SANKEY CHART COMPLETE ===');
    
  } catch (error) {
    console.error('Error creating Sankey chart:', error);
    showChartError('Chart rendering error');
    visualizationState.isRendering = false;
  }
}

/**
 * Handle node click - currently disabled since we show all top 25 items by default
 */
function handleNodeClick(d) {
  // Node expansion is disabled - we now show top 25 items + Other groups by default
  console.log('Node click - expansion disabled, showing all top 25 items by default');
  return;
}

/**
 * Helper function for node tooltips
 */
function showNodeTooltip(event, d, budgetData, totalFlow, getColorFunc) {
  // Always use original value for tooltip display and calculations
  const value = d.originalValue || d.value;
  
  let percentage, percentageLabel;
  if (d.type === 'income-source') {
    percentage = ((value / totalFlow) * 100).toFixed(1);
    percentageLabel = 'of total income';
  } else if (d.type === 'total-income') {
    percentage = '100.0';
    percentageLabel = 'of total income';
  } else if (d.type === 'category') {
    const totalExpenses = budgetData.categories.reduce((sum, cat) => 
      sum + cat.items.reduce((catSum, item) => catSum + (item.amount || item.value || 0), 0), 0);
    percentage = ((value / totalExpenses) * 100).toFixed(1);
    percentageLabel = 'of total expenses';
  } else if (d.type === 'item') {
    // For items, calculate percentage of total expenses
    const totalExpenses = budgetData.categories.reduce((sum, cat) => 
      sum + cat.items.reduce((catSum, item) => catSum + (item.amount || item.value || 0), 0), 0);
    percentage = ((value / totalExpenses) * 100).toFixed(1);
    percentageLabel = 'of total expenses';
  } else if (d.type === 'other') {
    // For "Other" groups, calculate percentage of category
    let categoryTotal = 0;
    if (d.categoryName) {
      const category = budgetData.categories.find(cat => {
        const cleanCatName = cat.name.replace(/^[^\w\s]+\s*/, '').trim() || cat.name;
        return cleanCatName === d.categoryName;
      });
      if (category) {
        categoryTotal = category.items.reduce((sum, item) => {
          const amount = parseFloat(item.amount || item.value) || 0;
          const frequency = item.frequency || 'monthly';
          return sum + convertToMonthlyAmount(amount, frequency);
        }, 0);
      }
    }
    if (categoryTotal > 0) {
      percentage = ((value / categoryTotal) * 100).toFixed(1);
      percentageLabel = `of ${d.categoryName} budget`;
    } else {
      percentage = ((value / totalFlow) * 100).toFixed(1);
      percentageLabel = 'of total flow';
    }
  } else {
    // For items, calculate percentage of category
    let categoryTotal = 0;
    if (d.categoryName) {
      const category = budgetData.categories.find(cat => cat.name === d.categoryName);
      if (category) {
        categoryTotal = category.items.reduce((sum, item) => {
          const amount = parseFloat(item.amount || item.value) || 0;
          const frequency = item.frequency || 'monthly';
          return sum + convertToMonthlyAmount(amount, frequency);
        }, 0);
      }
    }
    if (categoryTotal > 0) {
      percentage = ((value / categoryTotal) * 100).toFixed(1);
      percentageLabel = 'of category budget';
    } else {
      percentage = ((value / totalFlow) * 100).toFixed(1);
      percentageLabel = 'of total flow';
    }
  }
  
  // Create tooltip content
  let tooltipContent = `
    <div style="font-weight: 600; margin-bottom: 4px; color: white;">
      ${d.name}
    </div>
    <div style="color: #27ae60; font-weight: 700; font-size: 1.1em;">
      ${formatCurrencyClean(value)}
    </div>
    <div style="color: #6c757d; font-size: 0.9em;">
      ${percentage}% ${percentageLabel}
    </div>
  `;
  
  
  // Category nodes now show top 25 items automatically - no expansion needed
  
  // Add line item details for category nodes
  if (d.type === 'category') {
    if (d.isUnallocated) {
      // Special handling for Unallocated category
      tooltipContent += `
        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #444;">
          <div style="font-size: 0.9em; color: #ccc;">Surplus income not allocated to any expense category</div>
        </div>
      `;
    } else {
      const category = budgetData.categories.find(cat => {
        const cleanName = cat.name.replace(/^[^\w\s]+\s*/, '').trim() || cat.name;
        return cleanName === d.name;
      });
      
      if (category && category.items && category.items.length > 0) {
        const validItems = category.items.filter(item => (item.amount || item.value || 0) > 0);
        
        if (validItems.length > 0) {
          tooltipContent += `
            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #444;">
              <div style="font-size: 0.9em; color: #ccc; margin-bottom: 4px;">Line items (${validItems.length}):</div>
          `;
          
          validItems.forEach(item => {
            const itemValue = item.amount || item.value || 0;
            const itemPercentage = ((itemValue / value) * 100).toFixed(1);
            tooltipContent += `
              <div style="font-size: 0.8em; color: #aaa; padding: 1px 0; display: flex; justify-content: space-between;">
                <span>• ${item.label}</span>
                <span style="margin-left: 8px;">${formatCurrencyClean(itemValue)} (${itemPercentage}%)</span>
              </div>
            `;
          });
          
          tooltipContent += `</div>`;
        }
      }
    }
  }
  
  // Add grouped items details if this is an "Other" node
  if (d.type === 'other' && d.otherItems && d.otherItems.length > 0) {
    tooltipContent += `
      <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #444;">
        <div style="font-size: 0.9em; color: #ccc; margin-bottom: 4px;">Contains ${d.otherItems.length} items:</div>
    `;
    
    // Sort items by value (descending) for consistent display
    const sortedItems = [...d.otherItems].sort((a, b) => b.monthlyValue - a.monthlyValue);
    
    sortedItems.forEach(item => {
      const itemPercentage = ((item.monthlyValue / value) * 100).toFixed(1);
      tooltipContent += `
        <div style="font-size: 0.8em; color: #aaa; padding: 1px 0; display: flex; justify-content: space-between;">
          <span>• ${item.name}</span>
          <span style="margin-left: 8px;">${formatCurrencyClean(item.monthlyValue)} (${itemPercentage}%)</span>
        </div>
      `;
    });
    
    tooltipContent += `</div>`;
  }
  
  // Pass node position info for consistent tooltip placement
  // Column 4 nodes (items and "other" groups) should show tooltips on the left
  const isColumn4Node = d.type === 'item' || d.type === 'other';
  showSankeyTooltip(event, tooltipContent, isColumn4Node);
}

/**
 * Enhanced mobile tooltip positioning
 */
function showSankeyTooltip(event, content, forceLeft = false) {
  const tooltip = document.getElementById('sankey-tooltip');
  if (!tooltip) return;
  
  tooltip.innerHTML = content;
  
  // Mobile-first positioning
  const isMobile = window.innerWidth <= 768;
  const buffer = isMobile ? 20 : 10;
  
  tooltip.style.position = 'absolute';
  tooltip.style.opacity = 1;
  tooltip.style.zIndex = '10000';
  
  // Immediate mobile adjustments
  if (isMobile) {
    tooltip.style.fontSize = '12px';
    tooltip.style.maxWidth = '200px';
    tooltip.style.padding = '8px 12px';
  }
  
  // Use pageX/Y for absolute positioning to account for scroll
  const tooltipX = event.pageX;
  const tooltipY = event.pageY;
  
  // Initial positioning - force left for rightmost nodes (categories)
  if (forceLeft) {
    tooltip.style.left = (tooltipX - 300 - buffer) + 'px';
    tooltip.style.top = (tooltipY - 20) + 'px';
  } else {
    tooltip.style.left = (tooltipX + buffer) + 'px';
    tooltip.style.top = (tooltipY + buffer) + 'px';
  }
  
  // Position adjustment after render
  setTimeout(() => {
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    if (forceLeft) {
      // For category nodes, keep tooltip to the left but adjust if needed
      if (tooltipRect.left < buffer) {
        // If forced left goes off screen, move it right but keep it left of cursor
        tooltip.style.left = (tooltipX - tooltipRect.width - buffer) + 'px';
      }
    } else {
      // Standard positioning logic for other nodes  
      // Check if tooltip goes beyond right edge of viewport
      if (tooltipX + tooltipRect.width + buffer > scrollLeft + viewportWidth) {
        tooltip.style.left = (tooltipX - tooltipRect.width - buffer) + 'px';
      }
    }
    
    // Vertical overflow check (same for all nodes)
    // Check if tooltip goes beyond bottom edge of viewport
    if (tooltipY + tooltipRect.height + buffer > scrollTop + viewportHeight) {
      tooltip.style.top = (tooltipY - tooltipRect.height - buffer) + 'px';
    }
    
    // Ensure tooltip stays within viewport
    const finalRect = tooltip.getBoundingClientRect();
    if (finalRect.left < buffer) {
      tooltip.style.left = buffer + 'px';
    }
    if (finalRect.top < buffer) {
      tooltip.style.top = buffer + 'px';
    }
    
    // On mobile, center horizontally if there's space (but not for forced left)
    if (isMobile && !forceLeft && tooltipRect.width < viewportWidth - (2 * buffer)) {
      const centerX = Math.max(buffer, Math.min(
        viewportWidth - tooltipRect.width - buffer,
        (viewportWidth - tooltipRect.width) / 2
      ));
      tooltip.style.left = centerX + 'px';
    }
  }, 10);
}

/**
 * Hide Sankey tooltip
 */
function hideSankeyTooltip() {
  const tooltip = document.getElementById('sankey-tooltip');
  if (tooltip) {
    tooltip.style.opacity = 0;
  }
}

/**
 * Show chart error
 */
function showChartError(message) {
  d3.select("#sankey-container")
    .append("div")
    .attr("class", "sankey-empty-state")
    .html(`
      <div class="icon">⚠️</div>
      <div class="message">${message}</div>
      <div class="submessage">Please try refreshing the page</div>
    `);
}

/**
 * Resize chart for mobile
 */
export function resizeChart() {
  if (!visualizationState.chartContainer) return;
  
  // Update mobile state
  visualizationState.isMobile = window.innerWidth <= 768;
  
  // Re-render chart
  renderSankeyDiagram();
}

/**
 * Switch bar chart mode between categories and expense items
 */
export function switchBarChartMode(mode) {
  // Update toggle button states
  document.querySelectorAll('.toggle-btn').forEach(btn => {
    btn.classList.remove('active');
  });
  
  if (mode === 'category') {
    document.getElementById('category-toggle').classList.add('active');
    document.getElementById('bar-chart-title').textContent = '📊 Largest Expenses (Category)';
  } else {
    document.getElementById('expense-item-toggle').classList.add('active');
    document.getElementById('bar-chart-title').textContent = '📊 Largest Expenses (Expense Item)';
  }
  
  // Store current mode for use in rendering
  visualizationState.barChartMode = mode;
  
  // Re-render the bar chart with new mode
  if (typeof captureBudgetData !== 'function') {
    console.error('captureBudgetData function not found!');
    return;
  }
  
  const budgetData = captureBudgetData();
  if (budgetData) {
    if (mode === 'category') {
      renderCategoryBarChart(budgetData);
    } else {
      renderExpenseItemBarChart(budgetData);
    }
  }
}

/**
 * Update flow summary cards with current budget data
 */
export function updateFlowSummaryCards(budgetData) {
  // If no budget data provided, try to capture it
  if (!budgetData) {
    if (typeof captureBudgetData !== 'function') {
      console.error('captureBudgetData function not found and no budget data provided!');
      return;
    }
    budgetData = captureBudgetData();
  }
  
  if (!budgetData) return;
  
  // Calculate totals
  const totalIncome = calculateTotalIncomeForFlow(budgetData);
  const { totalExpenses, totalSavingsItems } = calculateExpensesAndSavingsForFlow(budgetData);
  const unallocatedAmount = Math.max(0, totalIncome - totalExpenses - totalSavingsItems);
  const totalSavings = totalSavingsItems + unallocatedAmount;
  const savingsRate = totalIncome > 0 ? ((totalSavings / totalIncome) * 100) : 0;
  
  // Count sources and items
  const incomeSourceCount = budgetData.income ? budgetData.income.filter(item => (item.amount || 0) > 0).length : 0;
  const expenseItemCount = budgetData.categories ? 
    budgetData.categories.reduce((count, cat) => 
      count + cat.items.filter(item => (item.amount || item.value || 0) > 0).length, 0
    ) : 0;
  
  // Update the DOM elements
  const flowTotalIncomeEl = document.getElementById('flow-total-income');
  const flowTotalExpensesEl = document.getElementById('flow-total-expenses');
  const flowTotalSavingsEl = document.getElementById('flow-total-savings');
  const flowSavingsRateEl = document.getElementById('flow-savings-rate');
  const flowIncomeSourcesEl = document.getElementById('flow-income-sources');
  const flowExpenseItemsEl = document.getElementById('flow-expense-items');
  
  if (flowTotalIncomeEl) flowTotalIncomeEl.textContent = formatCurrencyClean(totalIncome);
  if (flowTotalExpensesEl) flowTotalExpensesEl.textContent = formatCurrencyClean(totalExpenses);
  if (flowTotalSavingsEl) flowTotalSavingsEl.textContent = formatCurrencyClean(totalSavings);
  if (flowSavingsRateEl) flowSavingsRateEl.textContent = `${savingsRate.toFixed(1)}%`;
  if (flowIncomeSourcesEl) flowIncomeSourcesEl.textContent = `(from ${incomeSourceCount} source${incomeSourceCount !== 1 ? 's' : ''})`;
  if (flowExpenseItemsEl) flowExpenseItemsEl.textContent = `(${expenseItemCount} Line Item${expenseItemCount !== 1 ? 's' : ''})`;
}

/**
 * Calculate total income for flow summary
 */
function calculateTotalIncomeForFlow(budgetData) {
  if (!budgetData.income) return 0;
  
  return budgetData.income.reduce((total, item) => {
    const amount = parseFloat(item.amount) || 0;
    const frequency = item.frequency || 'monthly';
    let monthlyAmount = convertToMonthlyAmount(amount, frequency);
    
    // Apply tax calculations if income is pre-tax
    if (item.taxType === 'pre-tax') {
      const federalRate = parseFloat(item.federalTax) || 22;
      const stateRate = parseFloat(item.stateTax) || 5;
      const ficaRate = 7.65;
      const totalTaxRate = federalRate + stateRate + ficaRate;
      monthlyAmount = monthlyAmount * (1 - totalTaxRate / 100);
    }
    
    return total + monthlyAmount;
  }, 0);
}

/**
 * Calculate total expenses and savings items separately for flow summary
 */
function calculateExpensesAndSavingsForFlow(budgetData) {
  if (!budgetData.categories) return { totalExpenses: 0, totalSavingsItems: 0 };
  
  let totalExpenses = 0;
  let totalSavingsItems = 0;
  
  budgetData.categories.forEach(category => {
    category.items.forEach(item => {
      const amount = parseFloat(item.amount || item.value) || 0;
      const frequency = item.frequency || 'monthly';
      const monthlyAmount = convertToMonthlyAmount(amount, frequency);
      
      // Check if item is marked as savings
      // Look for savings checkbox (stored as isSavings property from captureBudgetData)
      const isSavingsItem = item.isSavings === true;
      
      if (isSavingsItem) {
        totalSavingsItems += monthlyAmount;
      } else {
        totalExpenses += monthlyAmount;
      }
    });
  });
  
  return { totalExpenses, totalSavingsItems };
}

/**
 * Render expense item bar chart
 */
function renderExpenseItemBarChart(budgetData) {
  const container = document.getElementById('category-bar-chart');
  if (!container) return;
  
  // Clear existing chart
  d3.select("#category-bar-chart").selectAll("*").remove();
  
  const data = prepareExpenseItemBarData(budgetData);
  if (data.length === 0) return;
  
  // Use same rendering logic as category chart but with item data
  const isMobile = window.innerWidth <= 768;
  const isPortrait = window.innerWidth < window.innerHeight;
  const isMobilePortrait = isMobile && isPortrait;
  
  const containerRect = container.getBoundingClientRect();
  let margin, width, height, chartHeight;
  
  if (isMobile) {
    margin = { top: 20, right: 30, bottom: 40, left: 20 };
    width = containerRect.width - margin.left - margin.right;
    chartHeight = Math.max(400, data.length * 60); // Adjust height for horizontal bars
    height = chartHeight - margin.top - margin.bottom;
  } else {
    margin = { top: 20, right: 20, bottom: 60, left: 40 };
    width = containerRect.width - margin.left - margin.right;
    chartHeight = 250;
    height = chartHeight - margin.top - margin.bottom;
  }
  
  const svg = d3.select("#category-bar-chart")
    .append("svg")
    .attr("width", containerRect.width)
    .attr("height", chartHeight)
    .style("background", "white");
  
  const g = svg.append("g")
    .attr("transform", `translate(${margin.left},${margin.top})`);
  
  const colors = [
    '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
    '#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#16a085'
  ];
  
  // Create scales based on mobile/desktop orientation
  let xScale, yScale;
  
  if (isMobile) {
    // Mobile: Horizontal bars (items on Y-axis, values on X-axis)
    yScale = d3.scaleBand()
      .domain(data.map(d => d.name))
      .range([0, height])
      .padding(0.5); // Increase padding to make bars less tall
    
    xScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value)])
      .range([0, width]);
  } else {
    // Desktop: Vertical bars (items on X-axis, values on Y-axis)
    xScale = d3.scaleBand()
      .domain(data.map(d => d.name))
      .range([0, width])
      .padding(0.1);
    
    yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value)])
      .range([height, 0]);
  }
  
  // Create bars with orientation based on mobile/desktop
  g.selectAll('.bar-chart-bar')
    .data(data)
    .enter()
    .append('rect')
    .attr('class', 'bar-chart-bar')
    .attr('x', d => isMobile ? 0 : xScale(d.name))
    .attr('y', d => isMobile ? yScale(d.name) : yScale(d.value))
    .attr('width', d => isMobile ? xScale(d.value) : xScale.bandwidth())
    .attr('height', d => isMobile ? yScale.bandwidth() : height - yScale(d.value))
    .attr('fill', (d, i) => colors[i % colors.length]);
  
  if (isMobile) {
    // Mobile: Item name below bars, left-aligned
    g.selectAll('.bar-chart-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'bar-chart-label')
      .attr('x', 10)
      .attr('y', d => yScale(d.name) + yScale.bandwidth() + 20)
      .text(d => d.name.length > 20 ? d.name.substring(0, 18) + '...' : d.name)
      .style('text-anchor', 'start')
      .style('font-size', '12px')
      .style('font-weight', '600');
    
    // Mobile: Value below bars on same line, right-aligned
    g.selectAll('.bar-chart-value')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'bar-chart-value')
      .attr('x', width - 10)
      .attr('y', d => yScale(d.name) + yScale.bandwidth() + 20)
      .text(d => formatCurrencyClean(d.value))
      .style('text-anchor', 'end')
      .style('font-size', '12px')
      .style('fill', '#27ae60')
      .style('font-weight', '700');
  } else {
    // Desktop: Value labels on top of bars
    g.selectAll('.bar-chart-value')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'bar-chart-value')
      .attr('x', d => xScale(d.name) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.value) - 5)
      .text(d => formatCurrencyClean(d.value))
      .style('text-anchor', 'middle');
    
    // Desktop: Item labels below bars
    g.selectAll('.bar-chart-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'bar-chart-label')
      .attr('x', d => xScale(d.name) + xScale.bandwidth() / 2)
      .attr('y', height + 15)
      .text(d => d.name.length > 12 ? d.name.substring(0, 10) + '...' : d.name)
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', '600');
  }
}

/**
 * Prepare data for expense item bar chart
 */
function prepareExpenseItemBarData(budgetData) {
  if (!budgetData.categories) return [];
  
  const allItems = [];
  
  budgetData.categories.forEach(category => {
    category.items.forEach(item => {
      const amount = parseFloat(item.amount || item.value) || 0;
      const frequency = item.frequency || 'monthly';
      const monthlyAmount = convertToMonthlyAmount(amount, frequency);
      
      if (monthlyAmount > 0) {
        allItems.push({
          name: item.label || item.name || 'Unnamed Item',
          value: monthlyAmount,
          category: category.name
        });
      }
    });
  });
  
  // Sort by value descending and take top 10
  return allItems
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);
}

/**
 * ============================================================================
 * BUDGET VS ACTUAL DATA AGGREGATION FUNCTIONS
 * ============================================================================
 */

/**
 * Get actual expenses from Firestore for budget comparison
 * @param {Date} startDate - Start date for query
 * @param {Date} endDate - End date for query  
 * @param {string|null} budgetIdOrCategory - Budget ID or category name for filtering
 */
export async function getActualExpenses(startDate, endDate, budgetIdOrCategory = null) {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const currentBudgetId = getCurrentBudgetId();
    
    if (!currentBudgetId) {
      throw new Error('No budget ID available');
    }

    // Determine if the third parameter is a budget ID or category name
    let filterCategory = null;
    let budgetId = currentBudgetId;
    
    // If it's a string that looks like a category (not a UUID-like budget ID)
    if (budgetIdOrCategory && typeof budgetIdOrCategory === 'string') {
      if (budgetIdOrCategory.length < 10) {
        // Likely a category filter, keep current budget ID
        filterCategory = budgetIdOrCategory;
      } else {
        // Likely a budget ID
        budgetId = budgetIdOrCategory;
      }
    }

    // Include target user ID in cache key to ensure cache separation between accounts
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
    
    // Create cache key for this query
    const cacheKey = `getActualExpenses_${targetUserId}_${budgetId}_${filterCategory || 'all'}_${startDate.getTime()}_${endDate.getTime()}`;
    
    // Check cache first
    if (expenseCache.has(cacheKey)) {
      const cached = expenseCache.get(cacheKey);
      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.value;
      }
    }
    
    // Check for pending identical query to prevent race conditions
    if (pendingQueries.has(cacheKey)) {
      return await pendingQueries.get(cacheKey);
    }

    // Create query promise for pending tracking
    const queryPromise = (async () => {
      const queryStart = performance.now();
      
      // Build query
      let expensesQuery;
      
      if (filterCategory) {
        // Query with category filter
        expensesQuery = query(
          getUserExpenseCollection(),
          where('budgetId', '==', budgetId),
          where('categoryName', '==', filterCategory),
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'desc')
        );
      } else {
        // Query without category filter
        expensesQuery = query(
          getUserExpenseCollection(),
          where('budgetId', '==', budgetId),
          where('date', '>=', startDate),
          where('date', '<=', endDate),
          orderBy('date', 'desc')
        );
      }

      const snapshot = await getDocs(expensesQuery);
      let expenses = [];
      
      snapshot.forEach(doc => {
        expenses.push({
          id: doc.id,
          ...doc.data()
        });
      });

      // If we couldn't filter by categoryName directly (maybe it's stored differently),
      // filter the results manually
      if (filterCategory && expenses.length > 0) {
        const budgetData = getCurrentBudgetData();
        expenses = expenses.filter(expense => {
          // Check direct categoryName match first
          if (expense.categoryName === filterCategory) {
            return true;
          }
          
          // If no direct match, try to resolve category by categoryId
          if (expense.categoryId !== undefined && budgetData && budgetData.categories) {
            const categoryIndex = parseInt(expense.categoryId);
            const category = budgetData.categories[categoryIndex];
            if (category && category.name) {
              const cleanedCategoryName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
              return cleanedCategoryName === filterCategory;
            }
          }
          
          return false;
        });
      }

      const queryEnd = performance.now();
      
      // Cache result and remove from pending
      expenseCache.set(cacheKey, {
        value: expenses,
        timestamp: Date.now()
      });
      pendingQueries.delete(cacheKey);
      
      return expenses;
    })();
    
    // Track this query as pending
    pendingQueries.set(cacheKey, queryPromise);
    
    // Return the promise result
    const result = await queryPromise;
    console.log(`Retrieved ${result.length} expenses for period ${startDate} to ${endDate}${filterCategory ? ` filtered by category: ${filterCategory}` : ''}`);
    return result;
    
  } catch (error) {
    console.error('Error fetching actual expenses:', error);
    
    // If index is still building, provide helpful message
    if (error.code === 'failed-precondition' && error.message.includes('index')) {
      console.log('Firestore composite index is still building. Please wait a few minutes and try again.');
      console.log('Index URL:', error.message.match(/https:\/\/[^\s]+/)?.[0]);
    }
    
    return [];
  }
}

/**
 * Aggregate expenses by category and month
 */
export function aggregateExpensesByCategory(expenses, budgetData) {
  const aggregated = {};
  
  expenses.forEach(expense => {
    // Handle Firestore Timestamp objects
    let expenseDate;
    if (expense.date && typeof expense.date.toDate === 'function') {
      // Firestore Timestamp
      expenseDate = expense.date.toDate();
    } else if (expense.date instanceof Date) {
      // Already a Date object
      expenseDate = expense.date;
    } else if (typeof expense.date === 'string' || typeof expense.date === 'number') {
      // String or number timestamp
      expenseDate = new Date(expense.date);
    } else {
      console.error('Invalid date format for expense:', expense);
      return; // Skip this expense
    }
    
    const monthKey = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}`;
    
    console.log(`Processing expense: date=${expense.date}, parsed=${expenseDate.toISOString()}, monthKey=${monthKey}`);
    
    if (!aggregated[monthKey]) {
      aggregated[monthKey] = {};
    }
    
    // Get category name with fallbacks
    // Expenses store categoryId (array index), not categoryName
    let categoryName = expense.categoryName || expense.category || 'Uncategorized';
    
    // If we have a categoryId but no categoryName, look up the name from budget data
    if (!categoryName || categoryName === 'Uncategorized') {
      if (expense.categoryId !== undefined && budgetData && budgetData.categories) {
        const categoryIndex = parseInt(expense.categoryId);
        const category = budgetData.categories[categoryIndex];
        if (category && category.name) {
          // Clean the category name (remove emoji prefixes)
          categoryName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
        }
      }
    }
    
    console.log(`Expense category: categoryId="${expense.categoryId}", looked up="${categoryName}"`);
    
    if (!aggregated[monthKey][categoryName]) {
      aggregated[monthKey][categoryName] = {
        total: 0,
        lineItems: {},
        expenses: []
      };
    }
    
    // Add to category total (ensure amount is a number)
    const expenseAmount = parseFloat(expense.amount) || 0;
    aggregated[monthKey][categoryName].total += expenseAmount;
    aggregated[monthKey][categoryName].expenses.push(expense);
    
    // Aggregate by line item
    const lineItemName = expense.lineItemName || expense.lineItem || 'Other';
    if (!aggregated[monthKey][categoryName].lineItems[lineItemName]) {
      aggregated[monthKey][categoryName].lineItems[lineItemName] = {
        total: 0,
        expenses: []
      };
    }
    
    aggregated[monthKey][categoryName].lineItems[lineItemName].total += expenseAmount;
    aggregated[monthKey][categoryName].lineItems[lineItemName].expenses.push(expense);
  });
  
  return aggregated;
}

/**
 * Aggregate scheduled expenses by category
 */
export function aggregateScheduledExpensesByCategory(scheduledExpenses, budgetData) {
  const aggregated = {};
  
  scheduledExpenses.forEach(expense => {
    // Scheduled expenses use categoryName directly, but need to clean it
    let categoryName = expense.categoryName || 'Uncategorized';
    
    // Clean the category name (remove emoji prefixes) to match budget comparison
    const cleanCategoryName = categoryName.replace(/^[^\w\s]+\s*/, '').trim() || categoryName;
    
    console.log(`Processing scheduled expense: original="${categoryName}", cleaned="${cleanCategoryName}", amount=${expense.amount}`);
    
    if (!aggregated[cleanCategoryName]) {
      aggregated[cleanCategoryName] = {
        total: 0,
        expenses: []
      };
    }
    
    // Add to category total (ensure amount is a number)
    const expenseAmount = parseFloat(expense.amount) || 0;
    aggregated[cleanCategoryName].total += expenseAmount;
    aggregated[cleanCategoryName].expenses.push(expense);
  });
  
  return aggregated;
}

/**
 * Get current month budget vs actual data
 */
export async function getCurrentMonthBudgetVsActual(budgetId = null) {
  try {
    const funcStart = performance.now();
    console.log(`🕐 [Perf Debug] getCurrentMonthBudgetVsActual started at ${new Date().toISOString()}`);
    
    const currentDate = new Date();
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    console.log(`🕐 [Perf Debug] Fetching expenses from ${startOfMonth.toISOString()} to ${endOfMonth.toISOString()}`);
    
    // Get actual expenses for current month
    const expensesStart = performance.now();
    const expenses = await getActualExpenses(startOfMonth, endOfMonth, budgetId);
    const expensesEnd = performance.now();
    console.log(`🕐 [Perf Debug] getActualExpenses took ${(expensesEnd - expensesStart).toFixed(2)}ms, found ${expenses.length} expenses`);
    
    // Get scheduled recurring expenses for current month
    const recurringStart = performance.now();
    const recurringExpenses = await getCurrentMonthRecurringExpenses();
    const recurringEnd = performance.now();
    console.log(`🕐 [Perf Debug] getCurrentMonthRecurringExpenses took ${(recurringEnd - recurringStart).toFixed(2)}ms`);
    
    // Filter to only include this month to fix multi-month cache issue
    const filteredMonthRecurring = recurringExpenses.filter(expense => {
      if (!expense.dueDate) return false;
      const dueDate = new Date(expense.dueDate);
      return dueDate >= startOfMonth && dueDate <= endOfMonth;
    });
    
    const scheduledExpenses = filteredMonthRecurring.filter(expense => 
      expense.status === 'scheduled' || expense.status === 'pending'
    );
    
    console.log('📊 [Progress Debug] Filtered progress expenses:', {
      totalFromCache: recurringExpenses.length,
      filteredToMonth: filteredMonthRecurring.length,
      scheduledAfterFilter: scheduledExpenses.length,
      monthRange: `${startOfMonth.toISOString()} to ${endOfMonth.toISOString()}`,
      scheduledTotal: scheduledExpenses.reduce((sum, exp) => sum + exp.amount, 0)
    });
    console.log(`🕐 [Perf Debug] Found ${scheduledExpenses.length} scheduled expenses after filtering`);
    
    // Log individual expense structure
    if (expenses.length > 0) {
      console.log('Sample expense structure:', expenses[0]);
    }
    if (scheduledExpenses.length > 0) {
      console.log('Sample scheduled expense:', scheduledExpenses[0]);
    }
    
    const budgetDataStart = performance.now();
    const budgetData = captureBudgetData();
    const budgetDataEnd = performance.now();
    console.log(`🕐 [Perf Debug] captureBudgetData took ${(budgetDataEnd - budgetDataStart).toFixed(2)}ms`);
    
    // Aggregate expenses by category
    const aggregateStart = performance.now();
    const aggregatedExpenses = aggregateExpensesByCategory(expenses, budgetData);
    const aggregateEnd = performance.now();
    console.log(`🕐 [Perf Debug] aggregateExpensesByCategory took ${(aggregateEnd - aggregateStart).toFixed(2)}ms`);
    
    // Aggregate scheduled expenses by category using filtered data
    const scheduledAggStart = performance.now();
    const aggregatedScheduled = aggregateScheduledExpensesByCategory(scheduledExpenses, budgetData);
    const scheduledAggEnd = performance.now();
    console.log(`🕐 [Perf Debug] aggregateScheduledExpensesByCategory took ${(scheduledAggEnd - scheduledAggStart).toFixed(2)}ms`);
    
    const currentMonthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    console.log('Current month key:', currentMonthKey);
    
    const currentMonthExpenses = aggregatedExpenses[currentMonthKey] || {};
    console.log('Current month expenses:', currentMonthExpenses);
    
    // Build comparison data
    const comparisonData = [];
    
    budgetData.categories.forEach(category => {
      // Calculate monthly total using frequency conversion
      const categoryTotal = category.items.reduce((sum, item) => {
        const amount = parseFloat(item.amount) || 0;
        const frequency = item.frequency || 'monthly';
        const monthlyAmount = convertToMonthlyAmount(amount, frequency);
        return sum + monthlyAmount;
      }, 0);
      
      const cleanCategoryName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
      const actualData = currentMonthExpenses[cleanCategoryName] || { total: 0, lineItems: {}, expenses: [] };
      const scheduledData = aggregatedScheduled[cleanCategoryName] || { total: 0, expenses: [] };
      
      console.log(`Category "${cleanCategoryName}": budget=${categoryTotal} (monthly), actual=${actualData.total}, scheduled=${scheduledData.total}`);
      
      if (categoryTotal > 0 || actualData.total > 0 || scheduledData.total > 0) {
        comparisonData.push({
          categoryName: cleanCategoryName,
          budgetAmount: categoryTotal,
          actualAmount: actualData.total,
          scheduledAmount: scheduledData.total,
          variance: actualData.total - categoryTotal,
          percentageUsed: categoryTotal > 0 ? (actualData.total / categoryTotal) * 100 : 0,
          lineItems: category.items.map(item => {
            const amount = parseFloat(item.amount) || 0;
            const frequency = item.frequency || 'monthly';
            const monthlyAmount = convertToMonthlyAmount(amount, frequency);
            return {
              name: item.label,
              budgetAmount: monthlyAmount,
              originalAmount: amount,
              frequency: frequency,
              actualAmount: actualData.lineItems[item.label]?.total || 0,
              expenses: actualData.lineItems[item.label]?.expenses || []
            };
          }),
          allExpenses: actualData.expenses,
          scheduledExpenses: scheduledData.expenses
        });
      }
    });
    
    const funcEnd = performance.now();
    console.log(`🕐 [Perf Debug] getCurrentMonthBudgetVsActual completed in ${(funcEnd - funcStart).toFixed(2)}ms total`);
    
    return {
      month: currentMonthKey,
      categories: comparisonData,
      totals: {
        budgetTotal: comparisonData.reduce((sum, cat) => sum + cat.budgetAmount, 0),
        actualTotal: comparisonData.reduce((sum, cat) => sum + cat.actualAmount, 0),
        variance: comparisonData.reduce((sum, cat) => sum + cat.variance, 0)
      }
    };
    
  } catch (error) {
    console.error('Error getting current month budget vs actual:', error);
    return null;
  }
}

/**
 * Get historical budget vs actual data for multiple months
 */
export async function getHistoricalBudgetVsActual(monthsBack = 3, budgetId = null) {
  try {
    const currentDate = new Date();
    const historicalData = [];
    
    for (let i = monthsBack - 1; i >= 0; i--) {
      const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const startOfMonth = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
      const endOfMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
      
      // Get expenses for this month
      const expenses = await getActualExpenses(startOfMonth, endOfMonth, budgetId);
      const budgetData = captureBudgetData();
      const aggregatedExpenses = aggregateExpensesByCategory(expenses, budgetData);
      
      const monthKey = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;
      const monthExpenses = aggregatedExpenses[monthKey] || {};
      
      // Calculate totals for this month
      const budgetTotal = budgetData.categories.reduce((sum, cat) => 
        sum + cat.items.reduce((catSum, item) => catSum + (item.amount || 0), 0), 0);
      
      const actualTotal = Object.values(monthExpenses).reduce((sum, cat) => sum + cat.total, 0);
      
      // Calculate savings rate from raw expenses using isSavings flag for this month only
      let savingsTotal = 0;
      
      expenses.forEach(expense => {
        // Only count expenses from the current month
        let expenseDate;
        if (expense.date && expense.date.toDate) {
          // Firestore Timestamp object
          expenseDate = expense.date.toDate();
        } else if (expense.date) {
          // Regular Date object or string
          expenseDate = new Date(expense.date);
        } else {
          return;
        }
        
        const expenseMonthKey = `${expenseDate.getFullYear()}-${String(expenseDate.getMonth() + 1).padStart(2, '0')}`;
        
        if (expenseMonthKey === monthKey) {
          // Find the budget item to check if it's marked as savings
          const lineItemName = expense.lineItemName;
          let isSavingsItem = false;
          
          // Search through budget categories to find the matching item
          if (budgetData && budgetData.categories) {
            budgetData.categories.forEach(category => {
              if (category.items) {
                const matchingItem = category.items.find(item => item.label === lineItemName);
                if (matchingItem && matchingItem.isSavings) {
                  isSavingsItem = true;
                }
              }
            });
          }
          
          if (isSavingsItem) {
            savingsTotal += expense.amount || 0;
          }
        }
      });
      
      const savingsRate = actualTotal > 0 ? (savingsTotal / actualTotal) * 100 : 0;
      
      historicalData.push({
        month: monthKey,
        monthName: targetDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        budgetTotal,
        actualTotal,
        savingsTotal,
        savingsRate,
        variance: actualTotal - budgetTotal,
        percentageUsed: budgetTotal > 0 ? (actualTotal / budgetTotal) * 100 : 0
      });
    }
    
    return historicalData;
    
  } catch (error) {
    console.error('Error getting historical budget vs actual:', error);
    return [];
  }
}

/**
 * Calculate key metrics for budget performance
 */
// Add query caching with request deduplication
const expenseCache = new Map();
const pendingQueries = new Map(); // Track in-flight requests
const CACHE_DURATION = 30000; // 30 seconds

// Clear cache when expenses are updated
function clearExpenseCache() {
  expenseCache.clear();
  pendingQueries.clear();
}

// Export cache clearing function for use when expenses are added/updated
window.clearExpenseCache = clearExpenseCache;

/**
 * Calculate Daily Pace (Last 30 Days)
 */
async function calculateDailyPace() {
  try {
    const user = getCurrentUser();
    if (!user) return 0;
    
    const currentBudgetId = getCurrentBudgetId();
    
    // Include target user ID in cache key for linked accounts
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
    
    const cacheKey = `dailyPace_${targetUserId}_${currentBudgetId}`;
    
    // Check cache first
    if (expenseCache.has(cacheKey)) {
      const cached = expenseCache.get(cacheKey);
      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.value;
      }
    }
    
    // Check if query is already in progress
    if (pendingQueries.has(cacheKey)) {
      return await pendingQueries.get(cacheKey);
    }
    
    
    // Create and store the promise to prevent duplicate queries
    const queryPromise = (async () => {
      // Get expenses from last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const startTime = performance.now();
      const expensesQuery = query(
        getUserExpenseCollection(),
        where('budgetId', '==', currentBudgetId),
        where('date', '>=', thirtyDaysAgo),
        orderBy('date', 'desc')
      );
      
      const queryStartTime = performance.now();
      const expensesSnapshot = await getDocs(expensesQuery);
      const queryEndTime = performance.now();
      let totalLast30Days = 0;
      
      expensesSnapshot.forEach(doc => {
        const expense = doc.data();
        totalLast30Days += expense.amount || 0;
      });
      
      // Calculate result
      const dailyAverage = totalLast30Days / 30;
      
      // Cache result and remove from pending
      expenseCache.set(cacheKey, {
        value: dailyAverage,
        timestamp: Date.now()
      });
      pendingQueries.delete(cacheKey);
      
      return dailyAverage;
    })();
    
    // Store the promise to prevent duplicate queries
    pendingQueries.set(cacheKey, queryPromise);
    
    return await queryPromise;
    
  } catch (error) {
    console.error('Error calculating daily pace:', error);
    return 0;
  }
}

/**
 * Calculate Planned Spend (Next 7 Days)
 */
async function calculatePlannedSpend() {
  try {
    // Get scheduled/recurring expenses for next 7 days
    const currentDate = new Date();
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(currentDate.getDate() + 7);
    
    // Use the same approach as expense calendar - get current month expenses then filter
    const recurringExpenses = await getCurrentMonthRecurringExpenses();
    let plannedTotal = 0;
    
    if (recurringExpenses && recurringExpenses.length > 0) {
      recurringExpenses.forEach(expense => {
        // The getCurrentMonthRecurringExpenses returns objects with dueDate property
        if (expense.dueDate && expense.amount) {
          const expenseDate = expense.dueDate;
          
          // Check if expense falls within next 7 days and is still pending
          if (expenseDate >= currentDate && expenseDate <= sevenDaysFromNow && expense.status === 'pending') {
            plannedTotal += parseFloat(expense.amount) || 0;
          }
        }
      });
    }
    
    console.log('Calculated planned spend for next 7 days:', {
      dateRange: `${currentDate.toDateString()} to ${sevenDaysFromNow.toDateString()}`,
      recurringExpenses: recurringExpenses.length,
      plannedTotal: plannedTotal
    });
    
    return plannedTotal;
    
  } catch (error) {
    console.error('Error calculating planned spend:', error);
    return 0;
  }
}

export function calculateBudgetMetrics(budgetAmount, actualAmount, daysInMonth, currentDay) {
  const percentageUsed = budgetAmount > 0 ? (actualAmount / budgetAmount) * 100 : 0;
  const variance = actualAmount - budgetAmount;
  const isOverBudget = variance > 0;
  
  // Calculate spending pace
  const dailyBudget = budgetAmount / daysInMonth;
  const actualDailySpending = actualAmount / currentDay;
  const projectedMonthEnd = actualDailySpending * daysInMonth;
  
  // Calculate remaining budget and pace needed
  const remainingBudget = Math.max(0, budgetAmount - actualAmount);
  const remainingDays = daysInMonth - currentDay;
  const recommendedDailySpending = remainingDays > 0 ? remainingBudget / remainingDays : 0;
  
  return {
    percentageUsed: Math.round(percentageUsed * 10) / 10, // Round to 1 decimal
    variance,
    isOverBudget,
    projectedMonthEnd,
    dailyBudget,
    actualDailySpending,
    remainingBudget,
    remainingDays,
    recommendedDailySpending,
    isOnTrack: projectedMonthEnd <= budgetAmount * 1.05 // 5% tolerance
  };
}

/**
 * ============================================================================
 * BUDGET VS ACTUAL CHART RENDERING FUNCTIONS
 * ============================================================================
 */

/**
 * Render current month budget vs actual bar chart
 */
export async function renderBudgetVsActualChart(containerId = 'budget-vs-actual-container') {
  console.log('=== RENDERING BUDGET VS ACTUAL PROGRESS BARS ===');
  
  try {
    // Get current month data
    const comparisonData = await getCurrentMonthBudgetVsActual();
    if (!comparisonData) {
      showChartError('Unable to load budget comparison data');
      return;
    }
    
    // Clear previous content
    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`Container ${containerId} not found`);
      return;
    }
    
    container.innerHTML = '';
    
    // Create content wrapper (the visualization-box styling will be applied to the parent in HTML)
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'budget-vs-actual-content';
    
    // Create overall progress bar (no header needed)
    const overallProgress = createOverallProgressElement(comparisonData);
    contentWrapper.appendChild(overallProgress);
    
    // Filter and sort categories
    const categories = comparisonData.categories
      .filter(cat => cat.budgetAmount > 0 || cat.actualAmount > 0)
      .sort((a, b) => b.budgetAmount - a.budgetAmount);
    
    if (categories.length === 0) {
      const emptyState = document.createElement('div');
      emptyState.className = 'sankey-empty-state';
      emptyState.innerHTML = `
        <div class="icon">📊</div>
        <div class="message">No budget categories found</div>
        <div class="submessage">Add categories to your budget to see progress</div>
      `;
      contentWrapper.appendChild(emptyState);
      container.appendChild(contentWrapper);
      return;
    }
    
    // Create categories container
    const categoriesContainer = document.createElement('div');
    categoriesContainer.className = 'budget-categories-container';
    
    // Render each category progress bar
    categories.forEach(category => {
      const categoryElement = createCategoryProgressElement(category);
      categoriesContainer.appendChild(categoryElement);
    });
    
    contentWrapper.appendChild(categoriesContainer);
    container.appendChild(contentWrapper);
    
    // Update key metrics boxes at the top of the page
    updateKeyMetricsBoxes(comparisonData);
    
    console.log('Budget vs Actual progress bars rendered successfully');
    
  } catch (error) {
    console.error('Error rendering Budget vs Actual progress bars:', error);
    showChartError('Failed to render budget comparison');
  }
}

/**
 * Create overall progress element showing total budget performance
 */
function createOverallProgressElement(comparisonData) {
  const overallDiv = document.createElement('div');
  overallDiv.className = 'overall-progress-item';
  
  // Calculate totals including scheduled expenses
  const totalBudget = comparisonData.totals.budgetTotal || 0;
  const totalActual = comparisonData.totals.actualTotal || 0;
  const totalScheduled = comparisonData.categories.reduce((sum, cat) => sum + (cat.scheduledAmount || 0), 0);
  const totalCommitted = totalActual + totalScheduled;
  
  // Calculate percentages
  const actualPercentage = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;
  const scheduledPercentage = totalBudget > 0 ? (totalScheduled / totalBudget) * 100 : 0;
  const totalCommittedPercentage = totalBudget > 0 ? (totalCommitted / totalBudget) * 100 : 0;
  const remainingAmount = totalBudget - totalCommitted;
  
  // Calculate progress bar segments
  const withinBudgetPercentage = Math.min(actualPercentage, 100);
  const overBudgetPercentage = Math.max(0, actualPercentage - 100);
  
  overallDiv.innerHTML = `
    <div class="overall-progress-header">
      <h4 class="overall-name">Overall Budget</h4>
      <div class="overall-percentage">${Math.round(totalCommittedPercentage)}%</div>
    </div>
    
    <div class="progress-bar-container">
      <div class="overall-progress-bar">
        <div class="progress-fill ${totalScheduled > 0 ? 'left-rounded' : 'full-rounded'}" 
             style="width: ${totalCommittedPercentage > 100 ? (100 / Math.min(totalCommittedPercentage, 200)) * 100 : withinBudgetPercentage}%"
             data-tooltip="spent"
             data-amount="${totalActual}"
             data-category="Overall"></div>
        ${overBudgetPercentage > 0 ? `<div class="progress-fill over-budget right-rounded" 
             style="left: ${(100 / Math.min(totalCommittedPercentage, 200)) * 100}%; width: ${(Math.min(overBudgetPercentage, 100) / Math.min(totalCommittedPercentage, 200)) * 100}%"
             data-tooltip="over budget"
             data-amount="${totalActual - totalBudget}"
             data-category="Overall"></div>` : ''}
        ${totalScheduled > 0 ? `<div class="progress-scheduled ${totalActual > 0 ? 'right-rounded' : 'full-rounded'}" 
             style="left: ${totalCommittedPercentage > 100 ? (100 / Math.min(totalCommittedPercentage, 200)) * 100 : withinBudgetPercentage}%; width: ${totalCommittedPercentage > 100 ? (Math.min(scheduledPercentage, Math.max(0, 100 - Math.min(actualPercentage, 100))) / Math.min(totalCommittedPercentage, 200)) * 100 : Math.min(scheduledPercentage, 100 - Math.min(actualPercentage, 100))}%"
             data-tooltip="scheduled"
             data-amount="${totalScheduled}"
             data-category="Overall"></div>` : ''}
      </div>
    </div>
    
    <div class="overall-remaining ${remainingAmount < 0 ? 'over-budget' : ''}">
      ${remainingAmount < 0 ? `${formatCurrency(Math.abs(remainingAmount))} over budget` : `${formatCurrency(remainingAmount)} remaining`}
    </div>
  `;
  
  // Add tooltip event listeners
  setupProgressBarTooltips(overallDiv, comparisonData.categories);
  
  return overallDiv;
}

/**
 * Create a category progress element similar to expense tracker design
 */
function createCategoryProgressElement(category) {
  const categoryDiv = document.createElement('div');
  categoryDiv.className = 'category-progress-item';
  
  // Calculate amounts and percentages including scheduled expenses
  const scheduledAmount = category.scheduledAmount || 0;
  const totalCommitted = category.actualAmount + scheduledAmount;
  const actualPercentage = category.budgetAmount > 0 ? (category.actualAmount / category.budgetAmount) * 100 : 0;
  const scheduledPercentage = category.budgetAmount > 0 ? (scheduledAmount / category.budgetAmount) * 100 : 0;
  const totalCommittedPercentage = category.budgetAmount > 0 ? (totalCommitted / category.budgetAmount) * 100 : 0;
  const remainingAmount = category.budgetAmount - totalCommitted;
  
  // Calculate progress bar segments
  const withinBudgetPercentage = Math.min(actualPercentage, 100);
  const overBudgetPercentage = Math.max(0, actualPercentage - 100);
  
  categoryDiv.innerHTML = `
    <div class="category-progress-header">
      <h4 class="category-name">${category.categoryName}</h4>
      <div class="category-percentage">${Math.round(totalCommittedPercentage)}%</div>
    </div>
    
    <div class="progress-bar-container">
      <div class="category-progress-bar">
        <div class="progress-fill ${scheduledAmount > 0 ? 'left-rounded' : 'full-rounded'}" 
             style="width: ${totalCommittedPercentage > 100 ? (100 / Math.min(totalCommittedPercentage, 200)) * 100 : withinBudgetPercentage}%"
             data-tooltip="spent"
             data-amount="${category.actualAmount}"
             data-category="${category.categoryName}"></div>
        ${overBudgetPercentage > 0 ? `<div class="progress-fill over-budget right-rounded" 
             style="left: ${(100 / Math.min(totalCommittedPercentage, 200)) * 100}%; width: ${(Math.min(overBudgetPercentage, 100) / Math.min(totalCommittedPercentage, 200)) * 100}%"
             data-tooltip="over budget"
             data-amount="${category.actualAmount - category.budgetAmount}"
             data-category="${category.categoryName}"></div>` : ''}
        ${scheduledAmount > 0 ? `<div class="progress-scheduled ${category.actualAmount > 0 ? 'right-rounded' : 'full-rounded'}" 
             style="left: ${totalCommittedPercentage > 100 ? (100 / Math.min(totalCommittedPercentage, 200)) * 100 : withinBudgetPercentage}%; width: ${totalCommittedPercentage > 100 ? (Math.min(scheduledPercentage, Math.max(0, 100 - Math.min(actualPercentage, 100))) / Math.min(totalCommittedPercentage, 200)) * 100 : Math.min(scheduledPercentage, 100 - Math.min(actualPercentage, 100))}%"
             data-tooltip="scheduled"
             data-amount="${scheduledAmount}"
             data-category="${category.categoryName}"></div>` : ''}
      </div>
    </div>
    
    <div class="category-remaining-container">
      <div class="category-dropdown-indicator" data-category="${category.categoryName}">
        <span class="dropdown-arrow">▼</span>
      </div>
      <div class="category-remaining ${remainingAmount < 0 ? 'over-budget' : 'on-track'}">
        ${remainingAmount < 0 ? `${formatCurrency(Math.abs(remainingAmount))} over budget` : `${formatCurrency(remainingAmount)} remaining`}
      </div>
    </div>
    
    <div class="expense-items-list" id="expenses-${category.categoryName.replace(/[^a-zA-Z0-9]/g, '')}">
      <!-- Expense items will be populated here -->
    </div>
  `;
  
  // Add tooltip event listeners
  setupProgressBarTooltips(categoryDiv, [category]);
  
  // Add dropdown functionality
  setupCategoryDropdown(categoryDiv, category);
  
  return categoryDiv;
}

/**
 * Setup dropdown functionality for category expense items
 */
function setupCategoryDropdown(categoryDiv, category) {
  const dropdownIndicator = categoryDiv.querySelector('.category-dropdown-indicator');
  const expensesList = categoryDiv.querySelector('.expense-items-list');
  const arrow = categoryDiv.querySelector('.dropdown-arrow');
  
  dropdownIndicator.addEventListener('click', async (e) => {
    e.stopPropagation(); // Prevent triggering parent click handlers
    
    const isExpanded = expensesList.classList.contains('expanded');
    
    if (!isExpanded) {
      // Load and show expense items
      await loadCategoryExpenseItems(category, expensesList);
      expensesList.classList.add('expanded');
      arrow.classList.add('expanded');
    } else {
      // Hide expense items
      expensesList.classList.remove('expanded');
      arrow.classList.remove('expanded');
    }
  });
}

/**
 * Load and display expense items for a specific category
 */
async function loadCategoryExpenseItems(category, expensesList) {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) return;
    
    const budgetId = getCurrentBudgetId();
    if (!budgetId) return;
    
    // First, try to get expenses from the category data if available
    let expenses = [];
    
    // Check if category already has expense data from the main aggregation
    if (category.allExpenses && category.allExpenses.length > 0) {
      expenses = category.allExpenses;
      console.log(`Found ${expenses.length} expenses in category data for ${category.categoryName}`);
    } else {
      // Fallback: Query Firestore directly
      console.log(`Querying Firestore for expenses in ${category.categoryName}`);
      
      // Get current month date range
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      
      // Query expenses for this category in current month
      const expensesQuery = query(
        getUserExpenseCollection(),
        where('budgetId', '==', budgetId),
        where('categoryName', '==', category.categoryName),
        where('date', '>=', startOfMonth),
        where('date', '<=', endOfMonth),
        orderBy('date', 'desc')
      );
      
      const expensesSnapshot = await getDocs(expensesQuery);
      expenses = expensesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }
    
    // Render line item progress bars
    if (category.lineItems && category.lineItems.length > 0) {
      expensesList.innerHTML = category.lineItems.map(lineItem => {
        const budgetAmount = lineItem.budgetAmount || 0;
        const actualAmount = lineItem.actualAmount || 0;
        const percentage = budgetAmount > 0 ? (actualAmount / budgetAmount) * 100 : 0;
        const remainingAmount = budgetAmount - actualAmount;
        const isOverBudget = percentage > 100;
        const frequency = lineItem.frequency || 'monthly';
        
        // Calculate progress bar segments for line items
        const withinBudgetPercentage = Math.min(percentage, 100);
        const overBudgetPercentage = Math.max(0, percentage - 100);
        
        // Generate frequency markers for frequencies more frequent than monthly
        let frequencyMarkers = '';
        const frequencyDivisions = getFrequencyDivisions(frequency);
        if (frequencyDivisions > 1) {
          const markerPositions = [];
          for (let i = 1; i < frequencyDivisions; i++) {
            const position = (i / frequencyDivisions) * 100;
            markerPositions.push(position);
          }
          
          frequencyMarkers = markerPositions.map(position => 
            `<div class="frequency-marker" style="left: ${position}%"></div>`
          ).join('');
        }
        
        return `
          <div class="line-item-progress">
            <div class="line-item-progress-bar">
              <div class="line-item-fill ${overBudgetPercentage > 0 ? 'left-rounded' : 'full-rounded'}" 
                   style="width: ${percentage > 100 ? (100 / Math.min(percentage, 200)) * 100 : withinBudgetPercentage}%"></div>
              ${overBudgetPercentage > 0 ? `<div class="line-item-fill over-budget right-rounded" 
                   style="left: ${(100 / Math.min(percentage, 200)) * 100}%; width: ${(Math.min(overBudgetPercentage, 100) / Math.min(percentage, 200)) * 100}%"></div>` : ''}
              ${frequencyMarkers}
              <div class="line-item-overlay">
                <div class="line-item-name">${lineItem.name}</div>
                <div class="line-item-amounts">
                  ${formatCurrencyClean(actualAmount)} / ${formatCurrencyClean(budgetAmount)}
                </div>
              </div>
            </div>
          </div>
        `;
      }).join('');
    } else {
      // Fallback: show individual expense transactions if no line items
      if (expenses.length === 0) {
        expensesList.innerHTML = `
          <div class="no-expenses-message">
            No line items found for ${category.categoryName}
          </div>
        `;
      } else {
        expensesList.innerHTML = expenses.map(expense => {
          const expenseDate = expense.date.toDate ? expense.date.toDate() : new Date(expense.date);
          const formattedDate = expenseDate.toLocaleDateString("en-US", { 
            month: "short", 
            day: "numeric" 
          });
          
          return `
            <div class="expense-item">
              <div class="expense-item-info">
                <div class="expense-item-description">
                  ${expense.description || expense.lineItemName || 'Expense'}
                </div>
                <div class="expense-item-date">${formattedDate}</div>
              </div>
              <div class="expense-item-amount">
                ${formatCurrency(expense.amount)}
              </div>
            </div>
          `;
        }).join('');
      }
    }
    
  } catch (error) {
    console.error('Error loading category expense items:', error);
    expensesList.innerHTML = `
      <div class="no-expenses-message">
        Error loading expense items
      </div>
    `;
  }
}

/**
 * Update key metrics boxes with calculated values - Updated for 3 metrics design
 */
async function updateKeyMetricsBoxes(comparisonData) {
  try {
    // Calculate Total Spent (This Month)
    const totalSpent = comparisonData.totals.actualTotal || 0;
    
    // Calculate Daily Pace (Last 30 Days)
    const dailyPace = await calculateDailyPace();
    
    // Calculate Planned Spend (Next 7 Days)
    const plannedSpend = await calculatePlannedSpend();
    
    // Update Total Spent (This Month)
    const totalSpentValue = document.getElementById('total-spent-value');
    const totalSpentBox = document.getElementById('total-spent-metric');
    if (totalSpentValue) {
      totalSpentValue.textContent = `$${Math.round(totalSpent)}`; // No decimals
      totalSpentBox.className = 'metric-box';
    }
    
    // Update Daily Pace (Last 30 Days)
    const dailyPaceValue = document.getElementById('daily-pace-value');
    const dailyPaceBox = document.getElementById('daily-pace-metric');
    if (dailyPaceValue) {
      dailyPaceValue.textContent = `$${Math.round(dailyPace)}`; // No decimals
      
      // Color coding based on reasonable daily pace
      const budgetTotal = comparisonData.totals.budgetTotal || 0;
      const targetDailyPace = budgetTotal / 30; // Rough monthly budget / 30 days
      
      if (dailyPace <= targetDailyPace) {
        dailyPaceBox.className = 'metric-box positive';
      } else if (dailyPace <= targetDailyPace * 1.2) {
        dailyPaceBox.className = 'metric-box warning';
      } else {
        dailyPaceBox.className = 'metric-box negative';
      }
    }
    
    // Update Planned Spend (Next 7 Days)
    const plannedSpendValue = document.getElementById('planned-spend-value');
    const plannedSpendBox = document.getElementById('planned-spend-metric');
    if (plannedSpendValue) {
      plannedSpendValue.textContent = `$${Math.round(plannedSpend)}`; // No decimals
      plannedSpendBox.className = 'metric-box';
    }
    
    // Update Mobile Metrics Section (same values as desktop but different elements)
    const mobileTotalSpent = document.getElementById('mobile-total-spent-value');
    const mobileDailyPace = document.getElementById('mobile-daily-pace-value');
    const mobilePlannedSpend = document.getElementById('mobile-planned-spend-value');
    
    if (mobileTotalSpent) {
      mobileTotalSpent.textContent = `$${Math.round(totalSpent)}`;
    }
    
    if (mobileDailyPace) {
      mobileDailyPace.textContent = `$${Math.round(dailyPace)}`;
    }
    
    if (mobilePlannedSpend) {
      mobilePlannedSpend.textContent = `$${Math.round(plannedSpend)}`;
    }
    
  } catch (error) {
    console.error('Error updating key metrics:', error);
    
    // Fallback to show loading state
    ['total-spent-value', 'daily-pace-value', 'planned-spend-value', 
     'mobile-total-spent-value', 'mobile-daily-pace-value', 'mobile-planned-spend-value'].forEach(id => {
      const element = document.getElementById(id);
      if (element) element.textContent = '--';
    });
  }
}

/**
 * Show tooltip for budget bars
 */
function showBudgetTooltip(event, d, type) {
  const tooltip = document.getElementById('sankey-tooltip') || document.createElement('div');
  tooltip.id = 'sankey-tooltip';
  tooltip.className = 'sankey-tooltip';
  document.body.appendChild(tooltip);
  
  const currentDate = new Date();
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const currentDay = currentDate.getDate();
  
  const metrics = calculateBudgetMetrics(d.budgetAmount, d.actualAmount, daysInMonth, currentDay);
  
  let content = `
    <div style="font-weight: 600; margin-bottom: 4px; color: white;">
      ${d.categoryName}
    </div>
  `;
  
  if (type === 'budget') {
    content += `
      <div style="color: #3498db; font-weight: 700; font-size: 1.1em;">
        Budget: ${formatCurrencyClean(d.budgetAmount)}
      </div>
    `;
  } else {
    content += `
      <div style="color: #27ae60; font-weight: 700; font-size: 1.1em;">
        Actual: ${formatCurrencyClean(d.actualAmount)}
      </div>
      <div style="color: #6c757d; font-size: 0.9em;">
        ${metrics.percentageUsed}% of budget used
      </div>
    `;
    
    if (metrics.isOverBudget) {
      content += `
        <div style="color: #F68F4C; font-size: 0.9em; margin-top: 4px;">
          Over budget by ${formatCurrencyClean(Math.abs(metrics.variance))}
        </div>
      `;
    }
    
    content += `
      <div style="margin-top: 6px; font-size: 0.8em; color: #888; font-style: italic;">
        Click to drill down to line items
      </div>
    `;
  }
  
  tooltip.innerHTML = content;
  tooltip.style.position = 'fixed';
  tooltip.style.left = (event.clientX + 10) + 'px';
  tooltip.style.top = (event.clientY - 10) + 'px';
  tooltip.style.opacity = 1;
}

/**
 * Hide budget tooltip
 */
function hideBudgetTooltip() {
  const tooltip = document.getElementById('sankey-tooltip');
  if (tooltip) {
    tooltip.style.opacity = 0;
  }
}

/**
 * Handle category drill-down click
 */
function handleCategoryDrillDown(categoryData) {
  console.log('Drilling down to category:', categoryData.categoryName);
  // TODO: Implement drill-down to line items view
  // This will be implemented in the next step
}






/**
 * Setup tooltip functionality for progress bars
 */
function setupProgressBarTooltips(container, categories) {
  const progressBars = container.querySelectorAll("[data-tooltip]");
  
  progressBars.forEach(bar => {
    bar.addEventListener("mouseenter", (event) => {
      showProgressBarTooltip(event, bar, categories);
    });
    
    bar.addEventListener("mouseleave", () => {
      hideProgressBarTooltip();
    });
    
    bar.addEventListener("mousemove", (event) => {
      updateTooltipPosition(event);
    });
  });
}

/**
 * Show tooltip for progress bars
 */
function showProgressBarTooltip(event, bar, categories) {
  const tooltipType = bar.getAttribute("data-tooltip");
  const amount = parseFloat(bar.getAttribute("data-amount"));
  const categoryName = bar.getAttribute("data-category");
  
  let tooltip = document.getElementById("progress-tooltip");
  if (!tooltip) {
    tooltip = document.createElement("div");
    tooltip.id = "progress-tooltip";
    tooltip.className = "sankey-tooltip";
    document.body.appendChild(tooltip);
  }
  
  let content = "";
  
  if (tooltipType === "spent") {
    content = `
      <div style="font-weight: 600; margin-bottom: 4px; color: white;">
        ${categoryName} - Actual Spending
      </div>
      <div style="color: #F68F4C; font-weight: 700; font-size: 1.1em;">
        ${formatCurrencyClean(amount)}
      </div>
      <div style="color: #6c757d; font-size: 0.9em; margin-top: 4px;">
        Amount spent this month
      </div>
    `;
  } else if (tooltipType === "scheduled") {
    content = `
      <div style="font-weight: 600; margin-bottom: 4px; color: white;">
        ${categoryName} - Scheduled Expenses
      </div>
      <div style="color: #5dade2; font-weight: 700; font-size: 1.1em;">
        ${formatCurrencyClean(amount)}
      </div>
    `;
    
    // Add scheduled expense details
    const scheduledExpenses = getScheduledExpensesForCategory(categoryName, categories);
    if (scheduledExpenses.length > 0) {
      content += `
        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #eee;">
          <div style="font-size: 0.85em; color: #666; margin-bottom: 4px;">Upcoming expenses:</div>
      `;
      
      scheduledExpenses.forEach(expense => {
        if (!expense.dueDate) return; // Skip expenses without due dates
        const dueDate = expense.dueDate.toDate ? expense.dueDate.toDate() : new Date(expense.dueDate);
        const dueDateStr = dueDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        content += `
          <div style="font-size: 0.8em; color: #888; padding: 1px 0; display: flex; justify-content: space-between;">
            <span>• ${expense.label || expense.categoryName}</span>
            <span style="margin-left: 8px;">${formatCurrencyClean(expense.amount)} (${dueDateStr})</span>
          </div>
        `;
      });
      
      content += "</div>";
    }
  }
  
  tooltip.innerHTML = content;
  tooltip.style.opacity = "1";
  updateTooltipPosition(event);
}

/**
 * Hide progress bar tooltip
 */
function hideProgressBarTooltip() {
  const tooltip = document.getElementById("progress-tooltip");
  if (tooltip) {
    tooltip.style.opacity = "0";
  }
}

/**
 * Update tooltip position
 */
function updateTooltipPosition(event) {
  const tooltip = document.getElementById("progress-tooltip");
  if (!tooltip) return;
  
  tooltip.style.position = "fixed";
  tooltip.style.left = (event.clientX + 10) + "px";
  tooltip.style.top = (event.clientY - 10) + "px";
  tooltip.style.zIndex = "10000";
}

/**
 * Get scheduled expenses for a specific category
 */
function getScheduledExpensesForCategory(categoryName, categories) {
  if (categoryName === "Overall") {
    // For overall, get all scheduled expenses
    const allScheduled = [];
    categories.forEach(cat => {
      if (cat.scheduledExpenses && cat.scheduledExpenses.length > 0) {
        allScheduled.push(...cat.scheduledExpenses);
      }
    });
    return allScheduled.slice(0, 5); // Limit to 5 for overall view
  } else {
    // For specific category, get its scheduled expenses
    const category = categories.find(cat => cat.categoryName === categoryName);
    return category && category.scheduledExpenses ? category.scheduledExpenses : [];
  }
}

/**
 * ============================================================================
 * EXPENSE HISTORY FUNCTIONALITY
 * ============================================================================
 */

/**
 * Initialize expense history section
 */
function initializeExpenseHistory() {
  console.log('Initializing expense history...');
  
  // Set default date range (past 7 days)
  setDefaultDateRange();
  
  // Setup event listeners
  setupExpenseHistoryEventListeners();
  
  // Load initial data
  loadExpenseHistory();
}

/**
 * Set default date range to past 7 days
 */
function setDefaultDateRange() {
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);
  
  const fromInput = document.getElementById('expense-history-from');
  const toInput = document.getElementById('expense-history-to');
  
  if (fromInput) {
    fromInput.value = formatDateForInput(sevenDaysAgo);
  }
  
  if (toInput) {
    toInput.value = formatDateForInput(today);
  }
}

/**
 * Format date for input element (YYYY-MM-DD)
 */
function formatDateForInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Setup event listeners for expense history controls
 */
function setupExpenseHistoryEventListeners() {
  const fromInput = document.getElementById('expense-history-from');
  const toInput = document.getElementById('expense-history-to');
  
  if (fromInput) {
    fromInput.addEventListener('change', loadExpenseHistory);
  }
  
  if (toInput) {
    toInput.addEventListener('change', loadExpenseHistory);
  }
}

/**
 * Load and display expense history for the selected date range
 * @param {string} filterCategory - Optional category name to filter by
 */
export async function loadExpenseHistory(filterCategory = null) {
  console.log('Loading expense history...', filterCategory ? `Filtering by: ${filterCategory}` : '');
  
  try {
    const fromInput = document.getElementById('expense-history-from');
    const toInput = document.getElementById('expense-history-to');
    const contentElement = document.getElementById('expense-history-content');
    
    if (!fromInput || !toInput || !contentElement) {
      console.error('Required elements not found');
      return;
    }
    
    const fromDate = new Date(fromInput.value);
    const toDate = new Date(toInput.value);
    
    // Validate date range
    if (fromDate > toDate) {
      showExpenseHistoryError('From date cannot be after To date');
      return;
    }
    
    // Show loading state
    contentElement.innerHTML = `
      <div class="expense-history-loading">
        <div class="loading-spinner"></div>
        <div>Loading expenses...</div>
      </div>
    `;
    
    // Fetch expenses for date range
    const expenses = await getActualExpenses(fromDate, toDate, filterCategory);
    
    // Update header to show filter status
    updateExpenseHistoryHeader(filterCategory);
    
    if (expenses.length === 0) {
      showExpenseHistoryEmpty(filterCategory);
      return;
    }
    
    // Render expense history items
    renderExpenseHistoryItems(expenses);
    
  } catch (error) {
    console.error('Error loading expense history:', error);
    showExpenseHistoryError('Failed to load expense history');
  }
}

/**
 * Render expense history items
 */
function renderExpenseHistoryItems(expenses) {
  const contentElement = document.getElementById('expense-history-content');
  if (!contentElement) return;
  
  // Sort expenses by date (newest first)
  const sortedExpenses = expenses.sort((a, b) => {
    const dateA = a.date.toDate ? a.date.toDate() : new Date(a.date);
    const dateB = b.date.toDate ? b.date.toDate() : new Date(b.date);
    return dateB - dateA;
  });
  
  const expenseItems = sortedExpenses.map(expense => {
    const expenseDate = expense.date.toDate ? expense.date.toDate() : new Date(expense.date);
    const formattedDate = expenseDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    
    // Get category name - check both categoryName and categoryId
    let categoryName = expense.categoryName || 'Uncategorized';
    
    // If no categoryName but we have categoryId, look it up from budget data
    if ((!expense.categoryName || expense.categoryName === 'Uncategorized') && expense.categoryId !== undefined) {
      const budgetData = getCurrentBudgetData();
      if (budgetData && budgetData.categories) {
        const categoryIndex = parseInt(expense.categoryId);
        const category = budgetData.categories[categoryIndex];
        if (category && category.name) {
          // Clean the category name (remove emoji prefixes)
          categoryName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
        }
      }
    }
    
    return `
      <div class="expense-history-item" data-expense-id="${expense.id}">
        <div class="expense-item-info">
          <div class="expense-item-description">${expense.lineItemName || 'Expense'}</div>
          <div class="expense-item-meta">
            <span class="expense-item-category">${expense.description || ''}</span>
            <span class="expense-item-date">${formattedDate}</span>
          </div>
        </div>
        <div class="expense-item-right">
          <div class="expense-item-amount">${formatCurrency(expense.amount)}</div>
          <div class="expense-item-actions">
            <button class="expense-action-btn edit" onclick="editExpenseFromHistory('${expense.id}')" title="Edit expense">
              ✏️
            </button>
            <button class="expense-action-btn delete" onclick="deleteExpenseFromHistory('${expense.id}')" title="Delete expense">
              ✕
            </button>
          </div>
        </div>
      </div>
    `;
  }).join('');
  
  contentElement.innerHTML = expenseItems;
}

/**
 * Show empty state for expense history
 */
function showExpenseHistoryEmpty(filterCategory = null) {
  const contentElement = document.getElementById('expense-history-content');
  if (!contentElement) return;
  
  const message = filterCategory 
    ? `No expenses found for "${filterCategory}" in this time period`
    : 'No expenses found for this time period';
  
  const submessage = filterCategory
    ? 'Try removing the filter or adjusting your date range'
    : 'Try adjusting your date range or add some expenses';
  
  contentElement.innerHTML = `
    <div class="expense-history-empty">
      <div class="empty-icon">📋</div>
      <div class="empty-message">${message}</div>
      <div class="empty-submessage">${submessage}</div>
      ${filterCategory ? `<button class="clear-filter-btn" onclick="clearExpenseHistoryFilter()">Clear Filter</button>` : ''}
    </div>
  `;
}

/**
 * Update expense history header to show filter status
 */
function updateExpenseHistoryHeader(filterCategory = null) {
  const headerElement = document.querySelector('.expense-history-header h3');
  if (!headerElement) return;
  
  if (filterCategory) {
    headerElement.innerHTML = `📋 Expense History - ${filterCategory} <button class="clear-filter-btn" onclick="clearExpenseHistoryFilter()" style="margin-left: 8px; font-size: 12px; padding: 2px 8px;">Clear</button>`;
  } else {
    headerElement.innerHTML = '📋 Expense History';
  }
}

/**
 * Clear expense history filter
 */
function clearExpenseHistoryFilter() {
  loadExpenseHistory(); // Reload without filter
}

/**
 * Filter expense history by category
 */
function filterExpenseHistoryByCategory(categoryName) {
  loadExpenseHistory(categoryName);
}

/**
 * Show error state for expense history
 */
function showExpenseHistoryError(message) {
  const contentElement = document.getElementById('expense-history-content');
  if (!contentElement) return;
  
  contentElement.innerHTML = `
    <div class="expense-history-empty">
      <div class="empty-icon">⚠️</div>
      <div class="empty-message">Error Loading Expenses</div>
      <div class="empty-submessage">${message}</div>
    </div>
  `;
}

/**
 * Edit expense from history
 */
async function editExpenseFromHistory(expenseId) {
  console.log('Editing expense:', expenseId);
  
  try {
    // For now, show a simple prompt - this can be enhanced with a modal later
    const newDescription = prompt('Enter new description:');
    const newAmount = prompt('Enter new amount:');
    
    if (newDescription !== null && newAmount !== null) {
      const amount = parseFloat(newAmount);
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount');
        return;
      }
      
      // Update expense in Firestore
      const user = getCurrentUser();
      if (!user) {
        alert('User not authenticated');
        return;
      }
      
      const expenseRef = doc(db, 'expenses', expenseId);
      await updateDoc(expenseRef, {
        description: newDescription,
        amount: amount,
        updatedAt: new Date()
      });
      
      // Reload expense history
      await loadExpenseHistory();
      
      // Refresh other views if needed
      if (window.refreshAllProgressData) {
        await window.refreshAllProgressData();
      }
      
      console.log('Expense updated successfully');
    }
    
  } catch (error) {
    console.error('Error editing expense:', error);
    alert('Failed to update expense. Please try again.');
  }
}

/**
 * Delete expense from history
 */
async function deleteExpenseFromHistory(expenseId) {
  console.log('Deleting expense:', expenseId);
  
  try {
    // Show confirmation dialog
    if (!confirm('Are you sure you want to delete this expense? This action cannot be undone.')) {
      return;
    }
    
    const user = getCurrentUser();
    if (!user) {
      alert('User not authenticated');
      return;
    }
    
    // Use shared account logic
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
    
    // Delete expense from Firestore (user-scoped path)
    const expenseRef = doc(db, `users/${targetUserId}/expenses`, expenseId);
    await deleteDoc(expenseRef);
    
    // Reload expense history
    await loadExpenseHistory();
    
    // Refresh other views if needed
    if (window.refreshAllProgressData) {
      await window.refreshAllProgressData();
    }
    
    console.log('Expense deleted successfully');
    
  } catch (error) {
    console.error('Error deleting expense:', error);
    alert('Failed to delete expense. Please try again.');
  }
}

/**
 * Prepare data for category bar chart
 */
function prepareCategoryBarData(budgetData) {
  const categoryData = [];
  
  budgetData.categories.forEach(category => {
    const items = [];
    let categoryTotal = 0;
    
    category.items.forEach(item => {
      const amount = parseFloat(item.amount || item.value) || 0;
      const frequency = item.frequency || 'monthly';
      const monthlyAmount = convertToMonthlyAmount(amount, frequency);
      
      if (monthlyAmount > 0) {
        items.push({
          name: item.label || item.name || 'Unnamed Item',
          value: monthlyAmount,
          originalAmount: amount,
          frequency: frequency
        });
        categoryTotal += monthlyAmount;
      }
    });
    
    if (categoryTotal > 0 && items.length > 0) {
      const cleanName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
      
      // Sort items by value descending within each category
      items.sort((a, b) => b.value - a.value);
      
      categoryData.push({
        name: cleanName,
        value: categoryTotal,
        originalName: category.name,
        items: items
      });
    }
  });
  
  // Sort categories by total value descending
  categoryData.sort((a, b) => b.value - a.value);
  
  return categoryData;
}

/**
 * Render category bar chart with stacked expense items
 */
function renderCategoryBarChart(budgetData) {
  const container = document.getElementById('category-bar-chart');
  if (!container) return;
  
  // Clear existing chart
  d3.select("#category-bar-chart").selectAll("*").remove();
  
  const data = prepareCategoryBarData(budgetData);
  if (data.length === 0) return;
  
  // Detect mobile portrait mode for vertical orientation
  const isMobile = window.innerWidth <= 768;
  const isPortrait = window.innerWidth < window.innerHeight;
  const isMobilePortrait = isMobile && isPortrait;
  
  // Set dimensions based on orientation
  const containerRect = container.getBoundingClientRect();
  let margin, width, height, chartHeight;
  
  if (isMobilePortrait) {
    // Horizontal bars for mobile portrait
    margin = { top: 20, right: 30, bottom: 40, left: 20 }; // Reduced bottom since labels are inline
    width = containerRect.width - margin.left - margin.right;
    chartHeight = Math.max(400, data.length * 80); // More vertical space between bars
    height = chartHeight - margin.top - margin.bottom;
  } else {
    // Horizontal bars for desktop/landscape
    margin = { top: 20, right: 20, bottom: 60, left: 40 };
    width = containerRect.width - margin.left - margin.right;
    chartHeight = 250;
    height = chartHeight - margin.top - margin.bottom;
  }
  
  // Create SVG
  const svg = d3.select("#category-bar-chart")
    .append("svg")
    .attr("width", containerRect.width)
    .attr("height", chartHeight)
    .style("background", "white");
  
  const g = svg.append("g")
    .attr("transform", `translate(${margin.left},${margin.top})`);
  
  // Color scale for expense items
  const colors = [
    '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
    '#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#16a085',
    '#27ae60', '#2980b9', '#8e44ad', '#f1c40f', '#e74c3c'
  ];
  
  // Create scales based on mobile/desktop orientation
  let xScale, yScale;
  
  if (isMobile) {
    // Mobile: Horizontal bars (categories on Y-axis, values on X-axis)
    yScale = d3.scaleBand()
      .domain(data.map(d => d.name))
      .range([0, height])
      .padding(0.5); // Increase padding to make bars less tall
    
    xScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value)])
      .range([0, width]);
  } else {
    // Desktop: Vertical bars (categories on X-axis, values on Y-axis)
    xScale = d3.scaleBand()
      .domain(data.map(d => d.name))
      .range([0, width])
      .padding(0.1);
    
    yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value)])
      .range([height, 0]);
  }
  
  // Create bars with orientation based on mobile/desktop
  g.selectAll('.bar-chart-bar')
    .data(data)
    .enter()
    .append('rect')
    .attr('class', 'bar-chart-bar')
    .attr('x', d => isMobile ? 0 : xScale(d.name))
    .attr('y', d => isMobile ? yScale(d.name) : yScale(d.value))
    .attr('width', d => isMobile ? xScale(d.value) : xScale.bandwidth())
    .attr('height', d => isMobile ? yScale.bandwidth() : height - yScale(d.value))
    .attr('fill', (d, i) => colors[i % colors.length])
    .on('mouseover', function(event, d) {
      d3.select(this).attr('opacity', 0.8);
      showCategoryBarTooltip(event, d);
    })
    .on("mouseout", function(event, d) {
      d3.select(this).attr("opacity", 1);
      hideBarChartTooltip();
    });
  
  if (isMobile) {
    // Mobile: Category name below bars, left-aligned
    g.selectAll('.bar-chart-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'bar-chart-label')
      .attr('x', 10)
      .attr('y', d => yScale(d.name) + yScale.bandwidth() + 20)
      .text(d => d.name.length > 20 ? d.name.substring(0, 18) + '...' : d.name)
      .style('text-anchor', 'start')
      .style('font-size', '12px')
      .style('font-weight', '600');
    
    // Mobile: Value below bars on same line, right-aligned
    g.selectAll('.bar-chart-value')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'bar-chart-value')
      .attr('x', width - 10)
      .attr('y', d => yScale(d.name) + yScale.bandwidth() + 20)
      .text(d => formatCurrencyClean(d.value))
      .style('text-anchor', 'end')
      .style('font-size', '12px')
      .style('fill', '#27ae60')
      .style('font-weight', '700');
  } else {
    // Desktop: Value labels on top of bars
    g.selectAll('.bar-chart-value')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'bar-chart-value')
      .attr('x', d => xScale(d.name) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.value) - 5)
      .text(d => formatCurrencyClean(d.value))
      .style('text-anchor', 'middle');
    
    // Desktop: Category labels below bars
    g.selectAll('.bar-chart-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'bar-chart-label')
      .attr('x', d => xScale(d.name) + xScale.bandwidth() / 2)
      .attr('y', height + 15)
      .text(d => d.name.length > 12 ? d.name.substring(0, 10) + '...' : d.name)
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', '600');
  }
}

/**
 * Show category bar chart tooltip with line item breakdown
 */
function showCategoryBarTooltip(event, category) {
  const existingTooltip = document.querySelector('.bar-chart-tooltip');
  if (existingTooltip) {
    existingTooltip.remove();
  }
  
  const tooltip = document.createElement('div');
  tooltip.className = 'bar-chart-tooltip';
  tooltip.style.cssText = `
    position: fixed;
    background: var(--text-dark);
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    max-width: 300px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    z-index: 10000;
    pointer-events: none;
    left: ${event.clientX + 10}px;
    top: ${event.clientY - 10}px;
  `;
  
  // Sort line items by value (largest to smallest)
  const sortedItems = [...category.items].sort((a, b) => b.value - a.value);
  
  // Create tooltip content
  let content = `
    <div style="font-weight: 700; margin-bottom: 8px; color: white;">
      ${category.name}
    </div>
    <div style="color: #27ae60; font-weight: 700; font-size: 1.1em; margin-bottom: 12px;">
      ${formatCurrencyClean(category.value)}
    </div>
  `;
  
  // Add line items
  if (sortedItems.length > 0) {
    content += `<div style="border-top: 1px solid #444; padding-top: 8px;">`;
    content += `<div style="font-size: 0.9em; color: #ccc; margin-bottom: 6px;">Line items (${sortedItems.length}):</div>`;
    
    sortedItems.forEach(item => {
      const percentage = ((item.value / category.value) * 100).toFixed(1);
      content += `
        <div style="font-size: 0.85em; color: #aaa; padding: 2px 0; display: flex; justify-content: space-between; align-items: center;">
          <span>• ${item.name}</span>
          <span style="margin-left: 12px; color: #27ae60;">${formatCurrencyClean(item.value)} (${percentage}%)</span>
        </div>
      `;
    });
    
    content += `</div>`;
  }
  
  tooltip.innerHTML = content;
  document.body.appendChild(tooltip);
  
  // Adjust position if tooltip goes off screen
  const tooltipRect = tooltip.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  if (tooltipRect.right > viewportWidth) {
    tooltip.style.left = (event.clientX - tooltipRect.width - 10) + 'px';
  }
  
  if (tooltipRect.bottom > viewportHeight) {
    tooltip.style.top = (event.clientY - tooltipRect.height - 10) + 'px';
  }
}

/**
 * Hide bar chart tooltip
 */
function hideBarChartTooltip() {
  const tooltip = document.querySelector('.bar-chart-tooltip');
  if (tooltip) {
    tooltip.remove();
  }
}

/**
 * Show stacked bar chart tooltip for individual expense items
 */
function showStackedBarTooltip(event, item, category) {
  const existingTooltip = document.querySelector('.bar-chart-tooltip');
  if (existingTooltip) {
    existingTooltip.remove();
  }
  
  const tooltip = document.createElement('div');
  tooltip.className = 'bar-chart-tooltip';
  tooltip.style.cssText = `
    position: fixed;
    background: var(--text-dark);
    color: white;
    padding: 12px 16px;
    border-radius: 6px;
    font-size: 13px;
    pointer-events: none;
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.2s ease;
    max-width: 250px;
    line-height: 1.4;
  `;
  
  const itemPercentage = ((item.value / category.value) * 100).toFixed(1);
  const frequencyDisplay = item.frequency !== 'monthly' ? ` (${item.frequency})` : '';
  
  tooltip.innerHTML = `
    <div style="font-weight: 600; margin-bottom: 6px; color: #f8f9fa;">${category.name}</div>
    <div style="font-weight: 600; color: #3498db; margin-bottom: 4px;">${item.name}</div>
    <div style="color: #27ae60; font-weight: 700; margin-bottom: 4px;">${formatCurrencyClean(item.value)}/month</div>
    <div style="color: #95a5a6; font-size: 11px; margin-bottom: 2px;">${itemPercentage}% of category</div>
    ${item.frequency !== 'monthly' ? 
      `<div style="color: #95a5a6; font-size: 11px;">Original: ${formatCurrencyClean(item.originalAmount)}${frequencyDisplay}</div>` : 
      ''
    }
  `;
  
  document.body.appendChild(tooltip);
  
  // Position tooltip
  const tooltipRect = tooltip.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  let left = event.clientX - tooltipRect.width / 2;
  let top = event.clientY - tooltipRect.height - 15;
  
  // Adjust if tooltip goes off screen
  if (left < 10) left = 10;
  if (left + tooltipRect.width > viewportWidth - 10) {
    left = viewportWidth - tooltipRect.width - 10;
  }
  if (top < 10) {
    top = event.clientY + 15;
  }
  
  tooltip.style.left = left + 'px';
  tooltip.style.top = top + 'px';
  tooltip.style.opacity = '1';
}

/**
 * Show bar chart tooltip (legacy - kept for compatibility)
 */
function showBarChartTooltip(event, d) {
  showStackedBarTooltip(event, d, d);
}

// Mobile Tab Functionality
let currentMobileTab = 'category-breakdown';

/**
 * Setup mobile tab controls and event listeners
 */
function setupMobileTabs(isMobilePortrait) {
  const tabControls = document.getElementById('mobile-tab-controls');
  
  if (!tabControls) return;
  
  if (isMobilePortrait) {
    tabControls.style.display = 'flex';
    initializeTabEventListeners();
  } else {
    tabControls.style.display = 'none';
    // Reset to show all data when not in portrait mode
    currentMobileTab = 'all';
  }
}

/**
 * Initialize tab click event listeners
 */
function initializeTabEventListeners() {
  const tabButtons = document.querySelectorAll('.tab-btn');
  
  tabButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      const newTab = e.currentTarget.dataset.tab;
      if (newTab !== currentMobileTab) {
        switchToTab(newTab);
      }
    });
  });
}

/**
 * Switch to a specific tab and re-render the chart
 */
function switchToTab(tabName) {
  currentMobileTab = tabName;
  
  // Update active tab button
  const tabButtons = document.querySelectorAll('.tab-btn');
  tabButtons.forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tabName);
  });
  
  // Re-render the Sankey with filtered data
  renderSankeyDiagram();
}

/**
 * Filter nodes and links based on current mobile tab
 */
function filterDataForMobileTab(sankeyData) {
  if (currentMobileTab === 'all') {
    return sankeyData; // Return all data for desktop/portrait mode
  }
  
  const filteredNodes = [];
  const filteredLinks = [];
  const nodeIdMap = new Map();
  let newNodeIndex = 0;
  
  sankeyData.nodes.forEach(node => {
    let includeNode = false;
    
    switch (currentMobileTab) {
      case 'income-flow':
        includeNode = node.type === 'income-source' || node.type === 'total-income';
        break;
      case 'category-breakdown':
        includeNode = node.type === 'total-income' || node.type === 'category';
        break;
      case 'item-details':
        includeNode = node.type === 'category' || node.type === 'item' || node.type === 'other';
        break;
    }
    
    if (includeNode) {
      const newNode = { ...node, id: newNodeIndex };
      filteredNodes.push(newNode);
      nodeIdMap.set(node.id, newNodeIndex);
      newNodeIndex++;
    }
  });
  
  // Filter links to only include those between visible nodes
  sankeyData.links.forEach(link => {
    const sourceInMap = nodeIdMap.has(link.source);
    const targetInMap = nodeIdMap.has(link.target);
    
    if (sourceInMap && targetInMap) {
      filteredLinks.push({
        ...link,
        source: nodeIdMap.get(link.source),
        target: nodeIdMap.get(link.target)
      });
    }
  });
  
  return {
    nodes: filteredNodes,
    links: filteredLinks
  };
}

/**
 * Historical Trends Chart Implementation
 */

/**
 * Initialize historical trends chart
 */
export function initializeHistoricalTrends() {
  setupTimelineControls();
  renderHistoricalTrends(3); // Default to 3 months
}

/**
 * Setup timeline control buttons
 */
function setupTimelineControls() {
  const timelineButtons = document.querySelectorAll('.timeline-btn');
  
  timelineButtons.forEach(button => {
    button.addEventListener('click', () => {
      // Remove active class from all buttons
      timelineButtons.forEach(btn => btn.classList.remove('active'));
      
      // Add active class to clicked button
      button.classList.add('active');
      
      // Get months value and render chart
      const months = parseInt(button.dataset.months);
      renderHistoricalTrends(months);
    });
  });
}

/**
 * Render historical trends chart showing budget vs actual spending
 */
export async function renderHistoricalTrends(months = 3) {
  const container = document.getElementById('historical-trends-chart');
  const loading = document.getElementById('historical-trends-loading');
  const empty = document.getElementById('historical-trends-empty');
  
  if (!container) return;
  
  // Show loading state
  container.style.display = 'none';
  loading.style.display = 'flex';
  empty.style.display = 'none';
  
  try {
    // Get historical data
    const historicalData = await getHistoricalBudgetVsActual(months);
    
    if (!historicalData || historicalData.length === 0) {
      // Show empty state
      container.style.display = 'none';
      loading.style.display = 'none';
      empty.style.display = 'flex';
      return;
    }
    
    // Hide loading and empty states
    loading.style.display = 'none';
    empty.style.display = 'none';
    container.style.display = 'block';
    
    // Clear previous chart
    d3.select(container).selectAll('*').remove();
    
    // Chart dimensions
    const margin = { top: 20, right: 80, bottom: 70, left: 80 };
    const containerRect = container.getBoundingClientRect();
    const width = Math.max(400, containerRect.width) - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;
    
    // Create SVG
    const svg = d3.select(container)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);
    
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Scales
    const xScale = d3.scaleBand()
      .domain(historicalData.map(d => d.monthName))
      .range([0, width])
      .padding(0.3);
    
    // Y scale for dollar amounts (left axis)
    const yScale = d3.scaleLinear()
      .domain([0, d3.max(historicalData, d => Math.max(d.budgetTotal, d.actualTotal))])
      .range([height, 0])
      .nice();
    
    // Y scale for savings rate percentage (right axis)
    const yScaleSavings = d3.scaleLinear()
      .domain([0, 100])
      .range([height, 0]);
    
    // Color scheme
    const budgetColor = '#92C2E0'; // Light blue for budget bars
    const actualColor = '#ff8c42';  // Budgley orange for expense bars
    const savingsLineColor = '#2ecc71'; // Green for savings rate line
    
    // Create savings rate line generator
    const savingsLine = d3.line()
      .x(d => xScale(d.monthName) + xScale.bandwidth() / 2)
      .y(d => yScaleSavings(d.savingsRate))
      .curve(d3.curveMonotoneX);
    
    // Calculate bar width for stacked bars
    const barWidth = xScale.bandwidth() * 0.6;
    
    // Add X axis
    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('font-size', '11px')
      .style('text-anchor', 'end')
      .attr('dx', '-.5em')
      .attr('dy', '.3em')
      .attr('transform', 'rotate(-45)');
    
    // Add left Y axis (dollar amounts) - limited to 4 ticks
    g.append('g')
      .attr('class', 'y-axis-left')
      .call(d3.axisLeft(yScale).ticks(4).tickFormat(d => formatCurrencyClean(d)))
      .selectAll('text')
      .style('font-size', '12px')
      .style('fill', '#333');
    
    // Add right Y axis (savings rate percentage) - limited to 4 ticks
    g.append('g')
      .attr('class', 'y-axis-right')
      .attr('transform', `translate(${width}, 0)`)
      .call(d3.axisRight(yScaleSavings).ticks(4).tickFormat(d => d + '%'))
      .selectAll('text')
      .style('font-size', '12px')
      .style('fill', savingsLineColor);
    
    
    
    // Add grid lines - limited to 4 lines
    g.selectAll('.grid-line')
      .data(yScale.ticks(4))
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .style('stroke', '#e0e0e0')
      .style('stroke-width', 1)
      .style('stroke-dasharray', '2,2');
    
    // Add budget bars (background)
    g.selectAll('.budget-bar')
      .data(historicalData)
      .enter()
      .append('rect')
      .attr('class', 'budget-bar')
      .attr('x', d => xScale(d.monthName) + (xScale.bandwidth() - barWidth) / 2)
      .attr('y', d => yScale(d.budgetTotal))
      .attr('width', barWidth)
      .attr('height', d => height - yScale(d.budgetTotal))
      .attr('fill', budgetColor)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);
    
    // Add actual spending bars (foreground, filling budget bars)
    g.selectAll('.actual-bar')
      .data(historicalData)
      .enter()
      .append('rect')
      .attr('class', 'actual-bar')
      .attr('x', d => xScale(d.monthName) + (xScale.bandwidth() - barWidth) / 2)
      .attr('y', d => yScale(Math.min(d.actualTotal, d.budgetTotal)))
      .attr('width', barWidth)
      .attr('height', d => Math.min(d.actualTotal, d.budgetTotal) > 0 ? height - yScale(Math.min(d.actualTotal, d.budgetTotal)) : 0)
      .attr('fill', actualColor)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);
    
    // Add savings rate line
    g.append('path')
      .datum(historicalData)
      .attr('fill', 'none')
      .attr('stroke', savingsLineColor)
      .attr('stroke-width', 3)
      .attr('d', savingsLine);
    
    // Add savings rate points
    g.selectAll('.savings-point')
      .data(historicalData)
      .enter()
      .append('circle')
      .attr('class', 'savings-point')
      .attr('cx', d => xScale(d.monthName) + xScale.bandwidth() / 2)
      .attr('cy', d => yScaleSavings(d.savingsRate))
      .attr('r', 5)
      .attr('fill', savingsLineColor)
      .attr('stroke', 'white')
      .attr('stroke-width', 2);
    
    // Add legend above the chart
    const legend = g.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${(width - 300) / 2}, -10)`);
    
    // Budget legend item
    const budgetLegend = legend.append('g');
    budgetLegend.append('rect')
      .attr('x', 0)
      .attr('y', -6)
      .attr('width', 15)
      .attr('height', 12)
      .attr('fill', budgetColor)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);
    
    budgetLegend.append('text')
      .attr('x', 20)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .style('font-size', '11px')
      .style('fill', '#333')
      .text('Budget');
    
    // Actual legend item
    const actualLegend = legend.append('g')
      .attr('transform', 'translate(90, 0)');
    
    actualLegend.append('rect')
      .attr('x', 0)
      .attr('y', -6)
      .attr('width', 15)
      .attr('height', 12)
      .attr('fill', actualColor)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);
    
    actualLegend.append('text')
      .attr('x', 20)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .style('font-size', '11px')
      .style('fill', '#333')
      .text('Actual');
    
    // Savings rate legend item
    const savingsLegend = legend.append('g')
      .attr('transform', 'translate(170, 0)');
    
    savingsLegend.append('line')
      .attr('x1', 0)
      .attr('x2', 15)
      .attr('y1', 0)
      .attr('y2', 0)
      .attr('stroke', savingsLineColor)
      .attr('stroke-width', 3);
    
    savingsLegend.append('circle')
      .attr('cx', 7.5)
      .attr('cy', 0)
      .attr('r', 3)
      .attr('fill', savingsLineColor)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);
    
    savingsLegend.append('text')
      .attr('x', 20)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .style('font-size', '11px')
      .style('fill', '#333')
      .text('Savings Rate');
    
    // Add tooltips
    const tooltip = d3.select('body').append('div')
      .attr('class', 'visualization-tooltip')
      .style('opacity', 0);
    
    // Tooltip for budget bars
    g.selectAll('.budget-bar')
      .on('mouseover', function(event, d) {
        tooltip.transition()
          .duration(200)
          .style('opacity', .9);
        tooltip.html(`
          <strong>${d.monthName}</strong><br/>
          Budget: ${formatCurrency(d.budgetTotal)}
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });
    
    // Tooltip for actual bars
    g.selectAll('.actual-bar')
      .on('mouseover', function(event, d) {
        tooltip.transition()
          .duration(200)
          .style('opacity', .9);
        tooltip.html(`
          <strong>${d.monthName}</strong><br/>
          Actual: ${formatCurrency(d.actualTotal)}<br/>
          Variance: ${d.variance >= 0 ? '+' : ''}${formatCurrency(d.variance)}
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });
    
    // Tooltip for savings rate points
    g.selectAll('.savings-point')
      .on('mouseover', function(event, d) {
        tooltip.transition()
          .duration(200)
          .style('opacity', .9);
        tooltip.html(`
          <strong>${d.monthName}</strong><br/>
          Savings Rate: ${d.savingsRate.toFixed(1)}%<br/>
          Savings: ${formatCurrency(d.savingsTotal)}
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });
    
  } catch (error) {
    console.error('Error rendering historical trends:', error);
    
    // Show empty state on error
    container.style.display = 'none';
    loading.style.display = 'none';
    empty.style.display = 'flex';
  }
}

/**
 * PROJECTED BALANCE FUNCTIONALITY
 */

/**
 * Calculate the true starting balance based on user data
 */
async function calculateStartingBalance() {
  try {
    const user = getCurrentUser();
    if (!user) return 0;

    // Check if user is linked to another account
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;

    // First, attempt to migrate any localStorage balance to Firebase
    await migrateLocalStorageBalance();
    
    // Get user's last entered account balance from Firebase incomeUpdate subcollection
    let lastEnteredBalance = 0;
    let lastEnteredDate = null;
    
    try {
      const incomeUpdateCollection = collection(db, `users/${targetUserId}/incomeUpdate`);
      const incomeUpdateQuery = query(incomeUpdateCollection, orderBy('date', 'desc'), limit(1));
      const snapshot = await getDocs(incomeUpdateQuery);
      
      if (!snapshot.empty) {
        const latestUpdate = snapshot.docs[0].data();
        lastEnteredBalance = latestUpdate.amount || 0;
        lastEnteredDate = new Date(latestUpdate.date);
        console.log(`Found starting balance: $${lastEnteredBalance} from ${lastEnteredDate.toLocaleDateString()}`);
      } else {
        // No stored balance found - start at 0 and don't calculate historical income/expenses
        console.log('No user account balance found, starting at $0.00');
        return 0;
      }
    } catch (error) {
      console.error('Error fetching starting balance from Firebase:', error);
      return 0;
    }

    // Calculate all income since that date
    let totalIncome = 0;
    const budgetData = getCurrentBudgetData();
    
    if (budgetData && budgetData.income) {
      // Calculate income from start of year or last balance date
      const monthsSinceStart = getMonthsSince(lastEnteredDate);
      
      budgetData.income.forEach(item => {
        const monthlyAmount = convertToMonthlyAmount(item.amount, item.frequency || 'monthly');
        totalIncome += monthlyAmount * monthsSinceStart;
      });
    }

    // Calculate all expenses since that date (from expense tracker)
    let totalExpenses = 0;
    const expensesCollection = getUserExpenseCollection();
    
    if (expensesCollection) {
      const expensesQuery = query(
        expensesCollection,
        where('date', '>=', lastEnteredDate.toISOString().split('T')[0]),
        orderBy('date', 'asc')
      );
      
      const snapshot = await getDocs(expensesQuery);
      snapshot.forEach(doc => {
        const expense = doc.data();
        totalExpenses += expense.amount || 0;
      });
    }

    // Calculate true starting balance
    const calculatedBalance = lastEnteredBalance + totalIncome - totalExpenses;
    
    console.log(`Starting balance calculation:
      Last entered: $${lastEnteredBalance.toFixed(2)}
      Income since: $${totalIncome.toFixed(2)}
      Expenses since: $${totalExpenses.toFixed(2)}
      Calculated: $${calculatedBalance.toFixed(2)}`);
    
    return calculatedBalance;
    
  } catch (error) {
    console.error('Error calculating starting balance:', error);
    return 0;
  }
}

/**
 * Helper function to calculate months between two dates
 */
function getMonthsSince(startDate) {
  const now = new Date();
  const months = (now.getFullYear() - startDate.getFullYear()) * 12 + 
                 (now.getMonth() - startDate.getMonth());
  return Math.max(0, months);
}

/**
 * Calculate income payment dates based on timing configuration
 */
function calculateIncomePaymentDates(incomeItem, startDate, endDate) {
  const paymentDates = [];
  
  // Check if income has specific timing configuration
  if (incomeItem.timingPattern && (incomeItem.dayOfWeek || incomeItem.dayOfMonth)) {
    // Use timing configuration to calculate payment dates
    const frequency = incomeItem.frequency || 'monthly';
    
    if (incomeItem.timingPattern === 'day-of-month') {
      // Fixed day of month (e.g., 15th of each month)
      let dayOfMonth = parseInt(incomeItem.dayOfMonth) || 15;
      
      // Handle custom day input
      if (incomeItem.dayOfMonth === 'custom') {
        dayOfMonth = parseInt(incomeItem.customDayNumber) || 15;
      } else if (incomeItem.dayOfMonth === 'last') {
        dayOfMonth = 0; // Last day of month
      }
      
      // Generate payment dates for the period
      let currentDate = new Date(startDate);
      currentDate.setDate(1); // Start from first of month
      
      while (currentDate <= endDate) {
        let paymentDate;
        if (dayOfMonth === 0) {
          // Last day of month
          paymentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        } else {
          paymentDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayOfMonth);
        }
        
        // Only add if within our date range
        if (paymentDate >= startDate && paymentDate <= endDate) {
          paymentDates.push(new Date(paymentDate));
        }
        
        // Move to next period based on frequency
        if (frequency === 'monthly') {
          currentDate.setMonth(currentDate.getMonth() + 1);
        } else if (frequency === 'quarterly') {
          currentDate.setMonth(currentDate.getMonth() + 3);
        } else if (frequency === 'annual') {
          currentDate.setFullYear(currentDate.getFullYear() + 1);
        } else {
          // For other frequencies, just do monthly for now
          currentDate.setMonth(currentDate.getMonth() + 1);
        }
      }
    } else if (incomeItem.timingPattern === 'day-of-week') {
      // Specific day of week (e.g., every Friday)
      const dayOfWeek = parseInt(incomeItem.dayOfWeek) || 5; // Default to Friday
      
      // Use payment schedule start date if available, otherwise find first occurrence
      let currentDate;
      if (incomeItem.paymentScheduleStartDate) {
        // Parse start date as local date to avoid timezone issues
        const dateParts = incomeItem.paymentScheduleStartDate.split('-');
        currentDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        
        // If start date is before forecast period, calculate next occurrence in period
        if (currentDate < startDate) {
          const daysDiff = Math.floor((startDate - currentDate) / (24 * 60 * 60 * 1000));
          const intervalDays = frequency === 'weekly' ? 7 : 14; // bi-weekly
          const intervalsPassed = Math.floor(daysDiff / intervalDays);
          currentDate = new Date(currentDate);
          currentDate.setDate(currentDate.getDate() + ((intervalsPassed + 1) * intervalDays));
        }
      } else {
        // Find first occurrence of this day of week in the period
        currentDate = new Date(startDate);
        while (currentDate.getDay() !== (dayOfWeek === 7 ? 0 : dayOfWeek) && currentDate <= endDate) {
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
      
      // Add payment dates based on frequency
      while (currentDate <= endDate) {
        if (currentDate >= startDate) {
          paymentDates.push(new Date(currentDate));
        }
        
        // Move to next payment based on frequency
        if (frequency === 'weekly') {
          currentDate.setDate(currentDate.getDate() + 7);
        } else if (frequency === 'bi-weekly') {
          currentDate.setDate(currentDate.getDate() + 14);
        } else {
          // For monthly/quarterly/annual, find same day of week in next period
          if (frequency === 'monthly') {
            currentDate.setMonth(currentDate.getMonth() + 1);
          } else if (frequency === 'quarterly') {
            currentDate.setMonth(currentDate.getMonth() + 3);
          } else {
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
          
          // Adjust to correct day of week
          while (currentDate.getDay() !== (dayOfWeek === 7 ? 0 : dayOfWeek)) {
            currentDate.setDate(currentDate.getDate() + 1);
          }
        }
      }
    }
  } else {
    // No timing configuration - default to 1st of each month
    const currentMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const nextMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1);
    
    // Add current month if we haven't passed the 1st
    if (currentMonth >= startDate && currentMonth <= endDate) {
      paymentDates.push(currentMonth);
    }
    
    // Add next month if it falls within our window
    if (nextMonth >= startDate && nextMonth <= endDate) {
      paymentDates.push(nextMonth);
    }
  }
  
  return paymentDates;
}

/**
 * Calculate expense payment dates based on recurring configuration
 */
function calculateExpensePaymentDates(expenseItem, startDate, endDate) {
  const paymentDates = [];
  
  // Check if expense has recurring configuration
  if (expenseItem.recurringConfig) {
    const config = expenseItem.recurringConfig;
    const frequency = expenseItem.frequency || 'monthly';
    
    if (config.type === 'fixed' && config.dayOfMonth) {
      // Fixed day of month (e.g., 15th of each month)
      let dayOfMonth = parseInt(config.dayOfMonth);
      
      // Generate payment dates for the period
      let currentDate = new Date(startDate);
      currentDate.setDate(1); // Start from first of month
      
      while (currentDate <= endDate) {
        const paymentDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayOfMonth);
        
        // Only add if within our date range
        if (paymentDate >= startDate && paymentDate <= endDate) {
          paymentDates.push(new Date(paymentDate));
        }
        
        // Move to next period based on frequency
        if (frequency === 'monthly') {
          currentDate.setMonth(currentDate.getMonth() + 1);
        } else if (frequency === 'quarterly') {
          currentDate.setMonth(currentDate.getMonth() + 3);
        } else if (frequency === 'annual') {
          currentDate.setFullYear(currentDate.getFullYear() + 1);
        } else {
          currentDate.setMonth(currentDate.getMonth() + 1);
        }
      }
    } else if (config.type === 'relative' && config.dayOfWeek && config.weekOfMonth) {
      // Relative date (e.g., 2nd Tuesday of each month)
      const dayOfWeek = parseInt(config.dayOfWeek);
      const weekOfMonth = parseInt(config.weekOfMonth);
      
      let currentDate = new Date(startDate);
      currentDate.setDate(1); // Start from first of month
      
      while (currentDate <= endDate) {
        // Find the specific weekday occurrence in the month
        const paymentDate = findNthWeekdayInMonth(
          currentDate.getFullYear(), 
          currentDate.getMonth(), 
          dayOfWeek, 
          weekOfMonth
        );
        
        if (paymentDate && paymentDate >= startDate && paymentDate <= endDate) {
          paymentDates.push(new Date(paymentDate));
        }
        
        // Move to next period
        if (frequency === 'monthly') {
          currentDate.setMonth(currentDate.getMonth() + 1);
        } else if (frequency === 'quarterly') {
          currentDate.setMonth(currentDate.getMonth() + 3);
        } else {
          currentDate.setMonth(currentDate.getMonth() + 1);
        }
      }
    }
  } else {
    // No recurring configuration - skip for now (will handle separately)
    console.log(`Expense ${expenseItem.label} has no recurringConfig, skipping scheduled projection`);
  }
  
  return paymentDates;
}

/**
 * Helper function to find nth occurrence of a weekday in a month
 */
function findNthWeekdayInMonth(year, month, dayOfWeek, weekOfMonth) {
  const firstDayOfMonth = new Date(year, month, 1);
  const firstWeekday = firstDayOfMonth.getDay();
  
  // Calculate the date of the first occurrence of the target weekday
  let firstOccurrence = 1 + (dayOfWeek - firstWeekday + 7) % 7;
  
  // Calculate the date of the nth occurrence
  const targetDate = firstOccurrence + (weekOfMonth - 1) * 7;
  
  // Check if this date exists in the month
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
  if (targetDate <= lastDayOfMonth) {
    return new Date(year, month, targetDate);
  }
  
  return null;
}

/**
 * Build array of projected transactions for next 30 days
 */
async function buildProjectedTransactions() {
  const transactions = [];
  const startDate = new Date();
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 30);
  
  const budgetData = getCurrentBudgetData();
  if (!budgetData) return transactions;

  // Process income items using their built-in timing data
  if (budgetData.income) {
    budgetData.income.forEach(item => {
      // Use the item's built-in timing data
      const enhancedItem = {
        ...item,
        paymentScheduleStartDate: item.startDate
      };
      
      // Use the actual payment amount, not the monthly conversion
      const paymentAmount = item.amount;
      
      // Calculate payment dates based on timing configuration
      const paymentDates = calculateIncomePaymentDates(enhancedItem, startDate, endDate);
      
      paymentDates.forEach(paymentDate => {
        transactions.push({
          date: paymentDate,
          amount: paymentAmount,
          type: 'income',
          description: item.label || item.name || 'Income',
          recurring: item.frequency !== 'once'
        });
      });
    });
  }

  // Process expense categories - using recurring configuration
  if (budgetData.categories) {
    // First, add scheduled expenses with recurring configuration
    budgetData.categories.forEach(category => {
      if (category.items) {
        category.items.forEach(expense => {
          console.log(`Processing expense: ${expense.label}`, {
            recurringConfig: expense.recurringConfig,
            frequency: expense.frequency,
            amount: expense.amount
          });
          
          // Use the actual payment amount, not the monthly conversion
          const paymentAmount = expense.amount;
          
          // Calculate expense payment dates based on recurring configuration
          const expensePaymentDates = calculateExpensePaymentDates(expense, startDate, endDate);
          
          console.log(`Payment dates for ${expense.label}:`, expensePaymentDates);
          
          expensePaymentDates.forEach(paymentDate => {
            console.log(`Adding expense transaction: ${category.name}: ${expense.label} - $${paymentAmount} on ${paymentDate}`);
            transactions.push({
              date: paymentDate,
              amount: -paymentAmount, // Negative for expenses
              type: 'expense',
              description: `${category.name}: ${expense.label}`,
              recurring: expense.frequency !== 'once',
              scheduled: true
            });
          });
        });
      }
    });

    // Then, handle unscheduled expenses using remaining budget logic
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const remainingDaysInMonth = Math.max(1, Math.ceil((endOfMonth - now) / (1000 * 60 * 60 * 24)));

    // Calculate total budgeted amount and scheduled amount for current month
    let totalBudgeted = 0;
    let totalScheduled = 0;
    let totalUnscheduledBudgeted = 0;

    budgetData.categories.forEach(category => {
      if (category.items) {
        category.items.forEach(expense => {
          const monthlyAmount = convertToMonthlyAmount(expense.amount, expense.frequency || 'monthly');
          totalBudgeted += monthlyAmount;
          
          // Count as scheduled if it has recurring configuration
          if (expense.recurringConfig) {
            totalScheduled += monthlyAmount;
          } else {
            // Track unscheduled budget amounts
            totalUnscheduledBudgeted += monthlyAmount;
          }
        });
      }
    });

    // Get actual expenses paid this month from expense tracker
    let totalUnscheduledSpent = 0;
    try {
      const expensesCollection = getUserExpenseCollection();
      const startOfMonth = new Date(currentYear, currentMonth, 1);
      const endOfMonth = new Date(currentYear, currentMonth + 1, 0);
      
      const expensesQuery = query(
        expensesCollection,
        where('date', '>=', startOfMonth.toISOString().split('T')[0]),
        where('date', '<=', endOfMonth.toISOString().split('T')[0]),
        orderBy('date', 'asc')
      );
      
      const snapshot = await getDocs(expensesQuery);
      snapshot.forEach(doc => {
        const expense = doc.data();
        // Only count expenses that don't correspond to scheduled items
        // For now, count all actual expenses as unscheduled spending
        totalUnscheduledSpent += expense.amount || 0;
      });
      
      console.log(`Unscheduled expenses this month: budgeted=$${totalUnscheduledBudgeted}, spent=$${totalUnscheduledSpent}`);
    } catch (error) {
      console.error('Error fetching actual expenses:', error);
    }

    // Calculate remaining unscheduled amount after actual spending
    const remainingUnscheduled = Math.max(0, totalUnscheduledBudgeted - totalUnscheduledSpent);
    
    if (remainingUnscheduled > 0) {
      const dailyUnscheduledAmount = remainingUnscheduled / remainingDaysInMonth;
      
      // Add daily unscheduled expenses for remaining days in current month
      for (let i = 0; i < remainingDaysInMonth && startDate.getDate() + i <= endOfMonth.getDate(); i++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + i);
        
        transactions.push({
          date: date,
          amount: -dailyUnscheduledAmount,
          type: 'expense',
          description: 'Unscheduled expenses (daily average)',
          recurring: false,
          scheduled: false
        });
      }
    }

    // Handle unscheduled expenses for future months (September onwards in this case)
    const nextMonthStart = new Date(currentYear, currentMonth + 1, 1);
    
    if (endDate > nextMonthStart) {
      // Calculate total unscheduled budget per month (original logic)
      const monthlyUnscheduledBudget = totalUnscheduledBudgeted;
      
      // Process each future month in our projection window
      let futureMonth = new Date(nextMonthStart);
      
      while (futureMonth < endDate) {
        const futureMonthEnd = new Date(futureMonth.getFullYear(), futureMonth.getMonth() + 1, 0);
        const actualEndDate = futureMonthEnd < endDate ? futureMonthEnd : endDate;
        
        // Calculate days in this month that fall within our projection window
        const monthStartForCalc = futureMonth > startDate ? futureMonth : startDate;
        const daysInProjectionWindow = Math.ceil((actualEndDate - monthStartForCalc) / (1000 * 60 * 60 * 24)) + 1;
        
        if (monthlyUnscheduledBudget > 0 && daysInProjectionWindow > 0) {
          const dailyAmount = monthlyUnscheduledBudget / new Date(futureMonth.getFullYear(), futureMonth.getMonth() + 1, 0).getDate();
          
          // Add daily transactions for this future month
          for (let day = 0; day < daysInProjectionWindow; day++) {
            const transactionDate = new Date(monthStartForCalc);
            transactionDate.setDate(transactionDate.getDate() + day);
            
            if (transactionDate <= actualEndDate) {
              transactions.push({
                date: new Date(transactionDate),
                amount: -dailyAmount,
                type: 'expense',
                description: 'Unscheduled expenses (daily average)',
                recurring: false,
                scheduled: false
              });
            }
          }
        }
        
        // Move to next month
        futureMonth.setMonth(futureMonth.getMonth() + 1);
      }
    }
  }

  // Sort transactions by date
  transactions.sort((a, b) => a.date - b.date);
  
  return transactions;
}

/**
 * Render the projected balance line chart
 */
async function renderProjectedBalance() {
  try {
    console.log('Rendering projected balance chart...');
    
    // Calculate starting balance and get projected transactions
    const startingBalance = await calculateStartingBalance();
    const transactions = await buildProjectedTransactions();
    
    // Update starting balance display
    updateStartingBalanceDisplay(startingBalance);
    
    // Build daily balance array for charting
    const balanceData = buildDailyBalanceData(startingBalance, transactions);
    
    // Render the D3 chart
    renderBalanceLineChart(balanceData);
    
  } catch (error) {
    console.error('Error rendering projected balance:', error);
    showChartError('Failed to render projected balance');
  }
}

/**
 * Update the starting balance display
 */
function updateStartingBalanceDisplay(balance) {
  const balanceElement = document.getElementById('current-starting-balance');
  if (balanceElement) {
    balanceElement.textContent = formatCurrency(balance);
    balanceElement.className = `balance-amount ${balance >= 0 ? 'positive' : 'negative'}`;
  }
}

/**
 * Build daily balance data for charting
 */
function buildDailyBalanceData(startingBalance, transactions) {
  const data = [];
  const startDate = new Date();
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 30);
  
  let currentBalance = startingBalance;
  let currentDate = new Date(startDate);
  
  // Create entry for each day
  for (let i = 0; i <= 30; i++) {
    const dayDate = new Date(startDate);
    dayDate.setDate(dayDate.getDate() + i);
    
    // Find all transactions for this day
    const dayTransactions = transactions.filter(t => {
      const transDate = new Date(t.date);
      return transDate.toDateString() === dayDate.toDateString();
    });
    
    // Apply all transactions for this day
    dayTransactions.forEach(transaction => {
      currentBalance += transaction.amount;
    });
    
    data.push({
      date: new Date(dayDate),
      balance: currentBalance,
      transactions: dayTransactions,
      dayOfWeek: dayDate.toLocaleDateString('en-US', { weekday: 'short' }),
      dateString: dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    });
  }
  
  return data;
}

/**
 * Render the D3.js line chart for balance projection
 */
function renderBalanceLineChart(data) {
  // Clear existing chart
  const container = document.getElementById('projected-balance-chart');
  if (!container) return;
  
  container.innerHTML = '';
  
  // Set up dimensions
  const margin = { top: 20, right: 30, bottom: 60, left: 80 };
  const containerRect = container.getBoundingClientRect();
  const width = Math.max(300, containerRect.width - margin.left - margin.right);
  const height = 300;
  
  // Create SVG
  const svg = d3.select(container)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom);
  
  const g = svg.append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);
  
  // Set up scales
  const xScale = d3.scaleTime()
    .domain(d3.extent(data, d => d.date))
    .range([0, width]);
  
  // Calculate domain ensuring y-axis starts at 0 or below
  const [minBalance, maxBalance] = d3.extent(data, d => d.balance);
  const yMin = Math.min(0, minBalance);
  const yMax = Math.max(0, maxBalance);
  
  const yScale = d3.scaleLinear()
    .domain([yMin, yMax])
    .nice()
    .range([height, 0]);
  
  // Create line generator
  const line = d3.line()
    .x(d => xScale(d.date))
    .y(d => yScale(d.balance))
    .curve(d3.curveCardinal);

  // Create separate area generators for positive and negative shading
  const positiveArea = d3.area()
    .x(d => xScale(d.date))
    .y0(yScale(0)) // Baseline at zero for positive areas
    .y1(d => yScale(d.balance))
    .curve(d3.curveCardinal);

  const negativeArea = d3.area()
    .x(d => xScale(d.date))
    .y0(d => yScale(d.balance)) // Baseline at the actual balance for negative areas
    .y1(yScale(0)) // Fill up to zero line
    .curve(d3.curveCardinal);
  
  // Add axes
  g.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`)
    .call(d3.axisBottom(xScale)
      .tickFormat(d3.timeFormat('%m/%d'))
      .ticks(6));
  
  g.append('g')
    .attr('class', 'y-axis')
    .call(d3.axisLeft(yScale)
      .tickFormat(d => formatCurrencyClean(d))
      .ticks(6));
  
  // Add zero line if needed
  const zeroY = yScale(0);
  if (zeroY >= 0 && zeroY <= height) {
    g.append('line')
      .attr('class', 'zero-line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', zeroY)
      .attr('y2', zeroY)
      .style('stroke', '#ddd')
      .style('stroke-dasharray', '3,3')
      .style('opacity', 0.7);
  }
  
  // Create gradient definitions for the line color
  const defs = svg.append('defs');
  
  const positiveGradient = defs.append('linearGradient')
    .attr('id', 'positive-gradient')
    .attr('gradientUnits', 'userSpaceOnUse')
    .attr('x1', 0).attr('y1', height)
    .attr('x2', 0).attr('y2', 0);
  
  positiveGradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', '#27ae60')
    .attr('stop-opacity', 0.8);
  
  positiveGradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', '#2ecc71')
    .attr('stop-opacity', 1);
  
  const negativeGradient = defs.append('linearGradient')
    .attr('id', 'negative-gradient')
    .attr('gradientUnits', 'userSpaceOnUse')
    .attr('x1', 0).attr('y1', height)
    .attr('x2', 0).attr('y2', 0);
  
  negativeGradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', '#e74c3c')
    .attr('stop-opacity', 1);
  
  negativeGradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', '#c0392b')
    .attr('stop-opacity', 0.8);
  
  // Add area shading with conditional colors
  // Create segments for positive and negative areas
  let currentSegment = [];
  let currentType = null; // 'positive' or 'negative'
  
  for (let i = 0; i < data.length; i++) {
    const point = data[i];
    const isPositive = point.balance >= 0;
    const segmentType = isPositive ? 'positive' : 'negative';
    
    if (currentType === null || currentType === segmentType) {
      // Continue current segment
      currentSegment.push(point);
      currentType = segmentType;
    } else {
      // Transition - end current segment and start new one
      if (currentSegment.length > 0) {
        // Add intersection point at zero for smooth transition
        const prevPoint = currentSegment[currentSegment.length - 1];
        const zeroPoint = {
          date: new Date(prevPoint.date.getTime() + (point.date.getTime() - prevPoint.date.getTime()) / 2),
          balance: 0
        };
        currentSegment.push(zeroPoint);
        
        // Draw the completed segment
        g.append('path')
          .datum(currentSegment)
          .attr('class', `balance-area-${currentType}`)
          .attr('d', currentType === 'positive' ? positiveArea : negativeArea)
          .style('fill', currentType === 'positive' ? '#27ae60' : '#e74c3c')
          .style('opacity', 0.15);
      }
      
      // Start new segment
      currentSegment = [
        { date: new Date(data[i-1].date.getTime() + (point.date.getTime() - data[i-1].date.getTime()) / 2), balance: 0 },
        point
      ];
      currentType = segmentType;
    }
  }
  
  // Draw final segment
  if (currentSegment.length > 0) {
    g.append('path')
      .datum(currentSegment)
      .attr('class', `balance-area-${currentType}`)
      .attr('d', currentType === 'positive' ? positiveArea : negativeArea)
      .style('fill', currentType === 'positive' ? '#27ae60' : '#e74c3c')
      .style('opacity', 0.15);
  }
  
  // Add dashed forecast line
  g.append('path')
    .datum(data)
    .attr('class', 'balance-line')
    .attr('d', line)
    .style('fill', 'none')
    .style('stroke-width', 2)
    .style('stroke-linecap', 'round')
    .style('stroke', '#aaa') // Light gray color
    .style('stroke-dasharray', '5,3'); // Dashed pattern (5px dash, 3px gap)
  
  // Add data points
  g.selectAll('.balance-dot')
    .data(data) // Show markers for all days
    .enter().append('circle')
    .attr('class', 'balance-dot')
    .attr('cx', d => xScale(d.date))
    .attr('cy', d => yScale(d.balance))
    .attr('r', 4)
    .style('fill', d => d.balance >= 0 ? '#27ae60' : '#e74c3c')
    .style('stroke', '#fff')
    .style('stroke-width', 2);
  
  // Add tooltip
  const tooltip = d3.select('body').append('div')
    .attr('class', 'balance-tooltip')
    .style('position', 'absolute')
    .style('padding', '10px')
    .style('background', 'rgba(0, 0, 0, 0.8)')
    .style('color', 'white')
    .style('border-radius', '5px')
    .style('pointer-events', 'none')
    .style('opacity', 0);
  
  // Add invisible overlay for mouse interactions
  g.append('rect')
    .attr('width', width)
    .attr('height', height)
    .style('fill', 'none')
    .style('pointer-events', 'all')
    .on('mousemove', function(event) {
      const [mouseX] = d3.pointer(event);
      const date = xScale.invert(mouseX);
      
      // Find closest data point
      const bisect = d3.bisector(d => d.date).left;
      const index = bisect(data, date, 1);
      const d0 = data[index - 1];
      const d1 = data[index];
      const d = date - d0.date > d1.date - date ? d1 : d0;
      
      // Show tooltip
      tooltip.transition().duration(200).style('opacity', 1);
      
      const tooltipContent = `
        <div style="font-weight: bold;">${d.dateString}</div>
        <div style="color: ${d.balance >= 0 ? '#2ecc71' : '#e74c3c'};">
          Balance: ${formatCurrency(d.balance)}
        </div>
      `;
      
      tooltip.html(tooltipContent)
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 10) + 'px');
    })
    .on('mouseout', function() {
      tooltip.transition().duration(200).style('opacity', 0);
    });
  
  // Style the axes
  g.selectAll('.x-axis text')
    .style('font-size', '12px')
    .style('fill', '#666');
    
  g.selectAll('.y-axis text')
    .style('font-size', '12px')
    .style('fill', '#666');
    
  g.selectAll('.domain')
    .style('stroke', '#ddd');
    
  g.selectAll('.tick line')
    .style('stroke', '#eee');
  
  console.log('Projected balance chart rendered successfully');
}

/**
 * BALANCE EDIT FUNCTIONALITY
 */

/**
 * Toggle the balance edit form
 */
window.toggleBalanceEdit = function() {
  const editForm = document.getElementById('balance-edit-form');
  const editBtn = document.getElementById('edit-balance-btn');
  const currentBalance = document.getElementById('current-starting-balance');
  
  if (editForm && editBtn) {
    const isVisible = editForm.style.display !== 'none';
    
    if (!isVisible) {
      // Show edit form
      editForm.style.display = 'flex';
      editBtn.style.display = 'none';
      
      // Pre-fill current balance (remove $ and commas)
      const input = document.getElementById('starting-balance-input');
      if (input && currentBalance) {
        const currentValue = currentBalance.textContent.replace(/[$,]/g, '');
        input.value = currentValue;
        input.focus();
        input.select();
      }
    } else {
      cancelBalanceEdit();
    }
  }
};

/**
 * Save the new starting balance
 */
window.saveStartingBalance = async function() {
  const input = document.getElementById('starting-balance-input');
  const editForm = document.getElementById('balance-edit-form');
  const editBtn = document.getElementById('edit-balance-btn');
  
  if (!input) return;
  
  try {
    const user = getCurrentUser();
    if (!user) return;
    
    // Check if user is linked to another account
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
    
    const newBalance = parseCurrencyInput(input.value);
    const currentDate = new Date().toISOString();
    
    // Store in Firebase incomeUpdate subcollection for the target user
    const incomeUpdateCollection = collection(db, `users/${targetUserId}/incomeUpdate`);
    await addDoc(incomeUpdateCollection, {
      amount: newBalance,
      date: currentDate,
      type: 'balance_update',
      updatedBy: user.uid, // Track who made the update (useful for linked accounts)
      timestamp: new Date()
    });
    
    // Also store in localStorage for backward compatibility
    localStorage.setItem(`startingBalance_${targetUserId}`, newBalance.toString());
    localStorage.setItem(`startingBalanceDate_${targetUserId}`, currentDate);
    
    // Hide edit form
    editForm.style.display = 'none';
    editBtn.style.display = 'inline-block';
    
    // Refresh the projected balance chart with new starting balance
    await renderProjectedBalance();
    
    console.log(`Starting balance updated to $${newBalance.toFixed(2)} for user ${targetUserId}`);
    
  } catch (error) {
    console.error('Error saving starting balance:', error);
    alert('Failed to save starting balance. Please try again.');
  }
};

/**
 * Cancel balance edit
 */
window.cancelBalanceEdit = function() {
  const editForm = document.getElementById('balance-edit-form');
  const editBtn = document.getElementById('edit-balance-btn');
  
  if (editForm && editBtn) {
    editForm.style.display = 'none';
    editBtn.style.display = 'inline-block';
  }
};

/**
 * Setup currency input for balance editing
 */
function setupBalanceInput() {
  const input = document.getElementById('starting-balance-input');
  if (input) {
    setupCurrencyInput(input);
    
    // Handle Enter key
    input.addEventListener('keypress', function(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        saveStartingBalance();
      }
    });
    
    // Handle Escape key
    input.addEventListener('keydown', function(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelBalanceEdit();
      }
    });
  }
}

// Export the main function for external use
window.renderProjectedBalance = renderProjectedBalance;
window.setupBalanceInput = setupBalanceInput;
window.saveStartingBalanceUpdate = saveStartingBalanceUpdate;
